import axios from 'axios';
import { getRequestId, LABEL_REQUEST_ID } from '@sbt-web/request-id-handler';
import { morphism, createSchema } from 'morphism';

function to(promise, errorExt) {
    return promise
        .then((data) => [null, data])
        .catch((err) => {
        let result = err;
        if (errorExt) {
            result = { ...err, ...errorExt };
        }
        return [result, undefined];
    });
}

const clientFactory = (...args) => {
    return clientFactoryAndConfig(...args).instance;
};
/**
 * Axios interceptor to add the request-id header
 */
function interceptors(axios) {
    axios.interceptors.request.use((config) => {
        const reqId = getRequestId();
        if (reqId && config.headers && !config.headers[LABEL_REQUEST_ID]) {
            config.headers[LABEL_REQUEST_ID] = reqId;
        }
        return config;
    });
}
const clientFactoryAndConfig = (baseUrl, apiPath, channel, timeout = 60000) => {
    const config = {
        baseURL: `${baseUrl}${apiPath}`,
        timeout,
        headers: channel
            ? {
                'X-Subito-Channel': channel,
            }
            : undefined,
        responseType: 'json',
    };
    const axiosInstance = axios.create(config);
    interceptors(axiosInstance);
    return { instance: axiosInstance, config };
};

class GenericResponse {
    status;
    info;
    payload;
    headers;
    constructor(status, info, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payload, 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    headers) {
        this.status = status;
        this.info = info;
        this.payload = payload;
        this.headers = headers;
    }
}

var HTTPStatusCode;
(function (HTTPStatusCode) {
    HTTPStatusCode[HTTPStatusCode["Continue"] = 100] = "Continue";
    HTTPStatusCode[HTTPStatusCode["SwitchingProtocols"] = 101] = "SwitchingProtocols";
    HTTPStatusCode[HTTPStatusCode["Processing"] = 102] = "Processing";
    HTTPStatusCode[HTTPStatusCode["EarlyHints"] = 103] = "EarlyHints";
    /**
     * All `1xx` status codes.
     */
    HTTPStatusCode[HTTPStatusCode["InformationalResponses"] = 103] = "InformationalResponses";
    HTTPStatusCode[HTTPStatusCode["OK"] = 200] = "OK";
    HTTPStatusCode[HTTPStatusCode["Created"] = 201] = "Created";
    HTTPStatusCode[HTTPStatusCode["Accepted"] = 202] = "Accepted";
    HTTPStatusCode[HTTPStatusCode["NonAuthoritativeInformation"] = 203] = "NonAuthoritativeInformation";
    HTTPStatusCode[HTTPStatusCode["NoContent"] = 204] = "NoContent";
    HTTPStatusCode[HTTPStatusCode["ResetContent"] = 205] = "ResetContent";
    HTTPStatusCode[HTTPStatusCode["PartialContent"] = 206] = "PartialContent";
    HTTPStatusCode[HTTPStatusCode["MultiStatus"] = 207] = "MultiStatus";
    HTTPStatusCode[HTTPStatusCode["AlreadyReported"] = 208] = "AlreadyReported";
    HTTPStatusCode[HTTPStatusCode["IMUsed"] = 226] = "IMUsed";
    /**
     * All `2xx` status codes.
     */
    HTTPStatusCode[HTTPStatusCode["Success"] = 255] = "Success";
    HTTPStatusCode[HTTPStatusCode["MultipleChoices"] = 300] = "MultipleChoices";
    HTTPStatusCode[HTTPStatusCode["MovedPermanently"] = 301] = "MovedPermanently";
    HTTPStatusCode[HTTPStatusCode["Found"] = 302] = "Found";
    HTTPStatusCode[HTTPStatusCode["SeeOther"] = 303] = "SeeOther";
    HTTPStatusCode[HTTPStatusCode["NotModified"] = 304] = "NotModified";
    HTTPStatusCode[HTTPStatusCode["UseProxy"] = 305] = "UseProxy";
    HTTPStatusCode[HTTPStatusCode["SwitchProxy"] = 306] = "SwitchProxy";
    HTTPStatusCode[HTTPStatusCode["TemporaryRedirect"] = 307] = "TemporaryRedirect";
    HTTPStatusCode[HTTPStatusCode["PermanentRedirect"] = 308] = "PermanentRedirect";
    /**
     * All `3xx` status codes.
     */
    HTTPStatusCode[HTTPStatusCode["Redirection"] = 319] = "Redirection";
    HTTPStatusCode[HTTPStatusCode["BadRequest"] = 400] = "BadRequest";
    HTTPStatusCode[HTTPStatusCode["Unauthorized"] = 401] = "Unauthorized";
    HTTPStatusCode[HTTPStatusCode["PaymentRequired"] = 402] = "PaymentRequired";
    HTTPStatusCode[HTTPStatusCode["Forbidden"] = 403] = "Forbidden";
    HTTPStatusCode[HTTPStatusCode["NotFound"] = 404] = "NotFound";
    HTTPStatusCode[HTTPStatusCode["MethodNotAllowed"] = 405] = "MethodNotAllowed";
    HTTPStatusCode[HTTPStatusCode["NotAcceptable"] = 406] = "NotAcceptable";
    HTTPStatusCode[HTTPStatusCode["ProxyAuthenticationRequired"] = 407] = "ProxyAuthenticationRequired";
    HTTPStatusCode[HTTPStatusCode["RequestTimeout"] = 408] = "RequestTimeout";
    HTTPStatusCode[HTTPStatusCode["Conflict"] = 409] = "Conflict";
    HTTPStatusCode[HTTPStatusCode["Gone"] = 410] = "Gone";
    HTTPStatusCode[HTTPStatusCode["LengthRequired"] = 411] = "LengthRequired";
    HTTPStatusCode[HTTPStatusCode["PreconditionFailed"] = 412] = "PreconditionFailed";
    HTTPStatusCode[HTTPStatusCode["PayloadTooLarge"] = 413] = "PayloadTooLarge";
    HTTPStatusCode[HTTPStatusCode["URITooLong"] = 414] = "URITooLong";
    HTTPStatusCode[HTTPStatusCode["UnsupportedMediaType"] = 415] = "UnsupportedMediaType";
    HTTPStatusCode[HTTPStatusCode["RangeNotSatisfiable"] = 416] = "RangeNotSatisfiable";
    HTTPStatusCode[HTTPStatusCode["ExpectationFailed"] = 417] = "ExpectationFailed";
    HTTPStatusCode[HTTPStatusCode["ImATeapot"] = 418] = "ImATeapot";
    HTTPStatusCode[HTTPStatusCode["MisdirectedRequest"] = 421] = "MisdirectedRequest";
    HTTPStatusCode[HTTPStatusCode["UnprocessableEntity"] = 422] = "UnprocessableEntity";
    HTTPStatusCode[HTTPStatusCode["Locked"] = 423] = "Locked";
    HTTPStatusCode[HTTPStatusCode["FailedDependency"] = 424] = "FailedDependency";
    HTTPStatusCode[HTTPStatusCode["UpgradeRequired"] = 426] = "UpgradeRequired";
    HTTPStatusCode[HTTPStatusCode["PreconditionRequired"] = 428] = "PreconditionRequired";
    HTTPStatusCode[HTTPStatusCode["TooManyRequests"] = 429] = "TooManyRequests";
    HTTPStatusCode[HTTPStatusCode["RequestHeaderFieldsTooLarge"] = 431] = "RequestHeaderFieldsTooLarge";
    HTTPStatusCode[HTTPStatusCode["UnavailableForLegalReasons"] = 451] = "UnavailableForLegalReasons";
    /**
     * All `4xx` error codes.
     */
    HTTPStatusCode[HTTPStatusCode["ClientErrors"] = 511] = "ClientErrors";
    HTTPStatusCode[HTTPStatusCode["InternalServerError"] = 500] = "InternalServerError";
    HTTPStatusCode[HTTPStatusCode["NotImplemented"] = 501] = "NotImplemented";
    HTTPStatusCode[HTTPStatusCode["BadGateway"] = 502] = "BadGateway";
    HTTPStatusCode[HTTPStatusCode["ServiceUnavailable"] = 503] = "ServiceUnavailable";
    HTTPStatusCode[HTTPStatusCode["GatewayTimeout"] = 504] = "GatewayTimeout";
    HTTPStatusCode[HTTPStatusCode["HTTPVersionNotSupported"] = 505] = "HTTPVersionNotSupported";
    HTTPStatusCode[HTTPStatusCode["VariantAlsoNegotiates"] = 506] = "VariantAlsoNegotiates";
    HTTPStatusCode[HTTPStatusCode["InsufficientStorage"] = 507] = "InsufficientStorage";
    HTTPStatusCode[HTTPStatusCode["LoopDetected"] = 508] = "LoopDetected";
    HTTPStatusCode[HTTPStatusCode["NotExtended"] = 510] = "NotExtended";
    HTTPStatusCode[HTTPStatusCode["NetworkAuthenticationRequired"] = 511] = "NetworkAuthenticationRequired";
    /**
     * All `5xx` error codes.
     */
    HTTPStatusCode[HTTPStatusCode["ServerErrors"] = 511] = "ServerErrors";
})(HTTPStatusCode || (HTTPStatusCode = {}));

const unknownError = new GenericResponse(HTTPStatusCode.InternalServerError, 'Unknown error');
const invalidRequestError = new GenericResponse(HTTPStatusCode.BadRequest, 'Invalid request');
const invalidResponseError = new GenericResponse(HTTPStatusCode.BadRequest, 'Invalid response');
const notFoundResponseError = new GenericResponse(HTTPStatusCode.NotFound, 'Not found');
const requestTimeoutResponseError = new GenericResponse(HTTPStatusCode.RequestTimeout, 'Request timeout');
/**
 * Takes the results from the Axios call and maps them to a response.
 * If there is an error then an error response is mapped from it.
 * If there is a response then it is mapped using morphism.
 * Otherwise a generic error is returned.
 * @param err The error from the request
 * @param resp The response from the request
 * @param responseSchema The schema of the request to use with Morphism
 * @param model The model to populate
 */
function handleResponse(err, resp, responseSchema) {
    if (err) {
        return handleGenericResponse(err, undefined);
    }
    if (resp) {
        // @ts-expect-error to fix this issue we need an extensive refactor: AxionsResponse<any, any> is too "any" to math the types of morphism
        return morphism(responseSchema, resp);
    }
    return unknownError;
}
function handleGenericResponse(err, resp) {
    if (err) {
        if (err.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            return {
                status: err.response.status,
                info: err.response.statusText,
                payload: err.response.data,
            };
        }
        else if (err.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            return {
                status: 0,
                info: 'No response received from the server, network error',
                payload: err.message,
            };
        }
        else {
            // Something happened in setting up the request that triggered an Error
            return {
                status: -1,
                info: 'Error while setting up the network request',
                payload: err.message,
            };
        }
    }
    if (resp) {
        return {
            status: resp.status,
            info: resp.statusText,
            payload: resp.data,
            headers: resp.headers,
        };
    }
    return unknownError;
}

const genericResponseSchema = {
    info: 'statusText',
    status: 'status',
    payload: 'data',
    headers: 'headers',
};

const availableCohortsSchema = {
    name: 'name',
    displayName: 'display_name',
};
const userCohortAssociationSchema = {
    userEmail: 'user_email',
    cohort: {
        path: 'cohort',
        fn: morphism(availableCohortsSchema),
    },
    startDate: 'start_date',
    endDate: 'end_date',
};
const getAvailableCohortsSchema = {
    ...genericResponseSchema,
    cohorts: {
        path: 'data',
        fn: morphism(availableCohortsSchema),
    },
};
const getUserCohortAssociationsSchema = {
    ...genericResponseSchema,
    associations: {
        path: 'data',
        fn: morphism(userCohortAssociationSchema),
    },
};

const commonPathV2 = `/v2/private`;
class AdonisClient {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/', channel, timeout);
    }
    async createUserCohortAssociations(associations) {
        const [err, rawResp] = await to(this.httpClient.post(`${commonPathV2}/smartbumps/users`, associations.map((obj) => ({
            cohort: obj.cohort,
            user_email: obj.userEmail,
            start_date: obj.startDate,
            end_date: obj.endDate,
        }))));
        return handleGenericResponse(err, rawResp);
    }
    async getAvailableCohortsByUserEmail(params) {
        const [err, rawResp] = await to(this.httpClient.get(`${commonPathV2}/cohorts`, { params }));
        return handleResponse(err, rawResp, getAvailableCohortsSchema);
    }
    async getUserCohortAssociations(params) {
        const [err, rawResp] = await to(this.httpClient.get(`${commonPathV2}/smartbumps/users`, { params }));
        return handleResponse(err, rawResp, getUserCohortAssociationsSchema);
    }
    async deleteUserCohortAssociation(params) {
        const [err, rawResp] = await to(this.httpClient.delete(`${commonPathV2}/smartbumps/users`, {
            data: {
                user_email: params.userEmail,
                cohort: params.cohort,
            },
        }));
        return handleGenericResponse(err, rawResp);
    }
    async editUserCohortAssociation(params) {
        const [err, rawResp] = await to(this.httpClient.delete(`${commonPathV2}/smartbumps/users`, {
            data: {
                user_email: params.userEmail,
                cohort: params.cohort,
            },
            params: {
                start_date: params.startDate,
                end_date: params.endDate,
            },
        }));
        return handleGenericResponse(err, rawResp);
    }
}

const userAddressSchema = {
    address: 'address',
    addressLine2: 'address_line_2',
    id: 'id',
    name: 'name',
    number: 'number',
    postcode: 'postcode',
    province: 'province',
    state: 'state',
    surname: 'surname',
    town: 'town',
    phone: 'phone',
    valid: 'valid',
};
const getUserAddressResponseSchema = {
    ...genericResponseSchema,
    address: {
        path: 'data',
        fn: morphism(userAddressSchema),
    },
};
const getUserAddressesResponseSchema = {
    ...genericResponseSchema,
    addresses: {
        path: 'data.addresses',
        fn: morphism(userAddressSchema),
    },
};
const shippingCarrierSchema = {
    carrierId: 'carrier_id',
    carrierName: 'carrier_name',
    carrierDescription: 'carrier_description',
    isPreselected: 'is_preselected',
    isRequired: 'is_required',
};
const dracoShippingCarrierSchema = {
    carrierId: 'carrier_id',
    carrierName: 'carrier_name',
    carrierShortName: 'carrier_short_name',
    carrierDescription: 'carrier_description',
    isPreselected: 'is_preselected',
    isAvailable: 'is_available',
    isRequired: 'is_required',
    availableOptionIds: 'available_option_ids',
};
const shippingOptionsSchema = {
    optionId: 'option_id',
    weight: 'weight',
    name: 'name',
    description: 'description',
    volumeDescription: 'volume_description',
    examples: 'examples',
    default: 'default',
    isAvailable: 'is_available',
};
const shippingTypesSchema = {
    typeId: 'type_id',
    default: 'default',
    isAvailable: 'is_available',
};
const shippingConfigurationSchema = {
    configurationType: 'configuration_type',
    serviceVersion: 'service_version',
    suggestedShippingPrice: 'suggested_shipping_price',
    shippingTypes: {
        path: 'shipping_type',
        fn: morphism(shippingTypesSchema),
    },
    shippingCarriers: {
        path: 'shipping_carriers',
        fn: morphism(dracoShippingCarrierSchema),
    },
    shippingOptions: {
        path: 'shipping_options',
        fn: morphism(shippingOptionsSchema),
    },
};
const getShippingConfigurationResponseSchema = {
    ...genericResponseSchema,
    shippingConfiguration: {
        path: 'data',
        fn: morphism(shippingConfigurationSchema),
    },
};
const getShippingCarriersResponseSchema = {
    ...genericResponseSchema,
    shippingCarriers: {
        path: 'data',
        fn: morphism(shippingCarrierSchema),
    },
};
const shippingRateSchema = {
    optionID: 'option_id',
    carrierID: 'carrier_id',
    carrierName: 'carrier_name',
    carrierShortName: 'carrier_short_name',
    destinationType: 'destination_type',
    total: 'total',
    strikethroughTotal: 'strikethrough_total',
};
const getShippingRatesResponseSchema = {
    ...genericResponseSchema,
    rates: {
        path: 'data.rates',
        fn: morphism(shippingRateSchema),
    },
};
const servicePointRecapSchema = {
    address: 'address',
    carrierID: 'carrier_id',
    carrierName: 'carrier_name',
    countryCode: 'country_code',
    name: 'name',
    postcode: 'postcode',
    servicePointID: 'service_point_id',
    town: 'town',
};
const servicePointSchema = {
    servicePointID: 'service_point_id',
    name: 'name',
    address: 'address',
    postcode: 'postcode',
    town: 'town',
    countryCode: 'country_code',
    carrierName: 'carrier_name',
    carrierID: 'carrier_id',
    distance: 'distance',
    lat: 'lat',
    lng: 'lng',
    hoursOfOperation: 'hours_of_operation',
};
const getServicePointsResponseSchema = {
    ...genericResponseSchema,
    servicePoints: {
        path: 'data',
        fn: morphism(servicePointSchema),
    },
};
const getServicePointResponseSchema = {
    ...genericResponseSchema,
    servicePoint: {
        path: 'data',
        fn: morphism(servicePointSchema),
    },
};

const convertToRawAddress = (address) => {
    const addressInfoRaw = {
        ...address,
        address_line_2: address.addressLine2,
    };
    delete addressInfoRaw.addressLine2;
    return addressInfoRaw;
};
class AeolusClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeoutMs = 500) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1', channel, timeoutMs);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async getUserAddresses(userId) {
        const [err, resp] = await to(this.httpClient.get(`/addresses/user/${userId}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getUserAddressesResponseSchema);
    }
    async addUserAddress(userId, addressInfo) {
        const addressInfoRaw = convertToRawAddress(addressInfo);
        const [err, resp] = await to(this.httpClient.post(`/addresses/user/${userId}`, addressInfoRaw, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getUserAddressResponseSchema);
    }
    async updateUserAddress(userId, addressId, addressInfo) {
        const addressInfoRaw = convertToRawAddress(addressInfo);
        const [err, resp] = await to(this.httpClient.patch(`/addresses/user/${userId}/address-id/${addressId}`, addressInfoRaw, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getUserAddressResponseSchema);
    }
    async getShippingCarriers(categoryId) {
        const [err, resp] = await to(this.httpClient.get(`/shipping/carriers`, {
            params: { category: categoryId },
        }));
        return handleResponse(err, resp, getShippingCarriersResponseSchema);
    }
    async getShippingConfiguration(categoryId, title) {
        const [err, resp] = await to(this.httpClient.get(`/shipping/configuration`, {
            params: { category_id: categoryId, title },
        }));
        return handleResponse(err, resp, getShippingConfigurationResponseSchema);
    }
    async getShippingRates(urn) {
        const [err, resp] = await to(this.httpClient.get(`/shipping/rates`, {
            params: {
                urn,
            },
        }));
        return handleResponse(err, resp, getShippingRatesResponseSchema);
    }
    async getServicePoints(geolocation, carriers) {
        const queryParams = {
            lat: geolocation[0],
            lng: geolocation[1],
            carriers: carriers.join(','),
        };
        const [err, resp] = await to(this.httpClient.get('/shipping/service_points', {
            params: queryParams,
            withCredentials: true,
        }));
        return handleResponse(err, resp, getServicePointsResponseSchema);
    }
    async getServicePoint(sp) {
        const [err, resp] = await to(this.httpClient.get(`/shipping/service_points/${sp.carrierID}/${sp.countryCode}/${sp.servicePointID}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getServicePointResponseSchema);
    }
}

// Request
const validateVoucherRequestProductsSchema = {
    product_id: 'productID',
    price: 'price',
};
const validateVoucherRequestItemSchema = {
    ad_id: 'adID',
    products: {
        path: 'products',
        fn: morphism(validateVoucherRequestProductsSchema),
    },
};
const validateVoucherRequestSchema = {
    items: {
        path: 'items',
        fn: morphism(validateVoucherRequestItemSchema),
    },
    type: 'type',
    entry_point: 'entryPoint',
};
// Response
const PromoAssetsSchema = {
    resourceName: 'resource_name',
    resourceUrl: 'resource_url',
};
const CountdownSchema = {
    alternativeSubtitle: 'alternative_subtitle',
};
const PromoContentSchema = createSchema({
    name: 'name',
    title: 'title',
    subtitle: 'subtitle',
    countdown: {
        path: 'countdown',
        fn: morphism(CountdownSchema),
    },
    assets: {
        path: 'assets',
        fn: morphism(PromoAssetsSchema),
    },
}, { undefinedValues: { strip: true } });
const PromoDiscountSchema = {
    receiptItem: 'receipt_item',
    type: 'type',
    value: 'value',
};
const promoSchema = {
    id: 'id',
    channels: 'channels',
    startDate: 'start_date',
    endDate: 'end_date',
    criteria: 'criteria',
    discounts: {
        path: 'discounts',
        fn: morphism(PromoDiscountSchema),
    },
    content: {
        path: 'content',
        fn: morphism(PromoContentSchema),
    },
};
const getPromoSchema = {
    ...genericResponseSchema,
    promo: {
        path: 'data.promotions',
        fn: morphism(promoSchema),
    },
};
const validationItemSchema = {
    productID: 'product_id',
    amount: 'amount',
    subtotalAmount: 'subtotal_amount',
};
const voucherValidationSchema = {
    amount: 'amount',
    totalAmount: 'total_amount',
    items: {
        path: 'items',
        fn: morphism(validationItemSchema),
    },
};
const validateVoucherResponseSchema = {
    ...genericResponseSchema,
    order: {
        path: 'data',
        fn: morphism(voucherValidationSchema),
    },
};
const bannerSchema = {
    bannerId: 'banner_id',
    title: 'title',
    description: 'description',
    code: 'code',
    placeholders: 'placeholders',
    icon: 'icon',
    color: 'color',
};
const getBannerSchema = {
    ...genericResponseSchema,
    banners: {
        path: 'data.banners',
        fn: morphism(bannerSchema),
    },
};
const RulesRawSchema = {
    categories: 'categories',
    channels: 'channels',
    entry_points: 'entrypoints',
    is_pro: 'isPro',
    pages: 'pages',
    placeholders: 'placeholders',
};
const toPrivateBannerRawSchema = {
    title: 'title',
    description: 'description',
    active_from: 'activeFrom',
    active_to: 'activeTo',
    code: 'code',
    icon: 'icon',
    color: 'color',
    enabled: 'enabled',
    rules: morphism(RulesRawSchema),
};
const privateBannerSchema = {
    bannerId: 'banner_id',
    title: 'title',
    description: 'description',
    activeFrom: 'active_from',
    activeTo: 'active_to',
    code: 'code',
    icon: 'icon',
    color: 'color',
    categories: 'categories',
    channels: 'channels',
    entrypoints: 'entry_points',
    enabled: 'enabled',
    isPro: 'is_pro',
    pages: 'pages',
    placeholders: 'placeholders',
};
const upsertBannerResponseSchema = {
    ...genericResponseSchema,
    banner: {
        path: 'data',
        fn: morphism(privateBannerSchema),
    },
};
const getBannersResponseSchema = {
    ...genericResponseSchema,
    count: 'data.count',
    banners: {
        path: 'data.banners',
        fn: morphism(privateBannerSchema),
    },
};

const DEFAULT_TIMEOUT_MS$4 = 80;
class AgonClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeout = DEFAULT_TIMEOUT_MS$4) {
        const args = [baseUrl, '/v1', channel, timeout];
        const clientAndConfig = clientFactoryAndConfig(...args);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async getActivePromo() {
        const [err, resp] = await to(this.httpClient.get(`/promotions`, {
            withCredentials: true,
            params: { active: true },
        }));
        return handleResponse(err, resp, getPromoSchema);
    }
    async validateVoucher(voucherCode, validateData) {
        const payload = morphism(validateVoucherRequestSchema, validateData);
        const [err, resp] = await to(this.httpClient.post(`/vouchers/validate/${voucherCode}`, payload, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, validateVoucherResponseSchema);
    }
    async getActiveBanners({ entry_point, page, categories, userId }, headers) {
        const [err, resp] = await to(this.httpClient.get(`/vouchers/banners`, {
            withCredentials: true,
            params: {
                entry_point,
                page,
                ...(categories ? { categories: categories.join(',') } : {}),
                ...(userId ? { userId } : {}),
            },
            headers,
        }));
        return handleResponse(err, resp, getBannerSchema);
    }
    async getBanners(params) {
        const [err, resp] = await to(this.httpClient.get(`/private/vouchers/banners`, {
            withCredentials: true,
            params,
        }));
        return handleResponse(err, resp, getBannersResponseSchema);
    }
    async createBanner(banner) {
        const body = morphism(toPrivateBannerRawSchema, banner);
        const [err, resp] = await to(this.httpClient.post(`/private/vouchers/banners`, body, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, upsertBannerResponseSchema);
    }
    async updateBanner(banner) {
        const body = morphism(toPrivateBannerRawSchema, banner);
        const [err, resp] = await to(this.httpClient.put(`/private/vouchers/banners/${banner.bannerId}`, body, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, upsertBannerResponseSchema);
    }
    async setBannerUsers({ bannerId, users, }) {
        const [err, resp] = await to(this.httpClient.put(`/private/vouchers/banners/${bannerId}/users`, { users }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}
const applyDiscount = (price, discount) => {
    if (!discount)
        return price;
    if (discount.type === 'fixed')
        return discount.value;
    if (discount.type === 'amount')
        return Math.max(price - discount.value, 0);
    const discountInEUR = Math.floor((price * discount.value) / 100);
    const discountedPrice = price - discountInEUR;
    return discountedPrice - (discountedPrice % 5);
};

const feedbackUserTokenSchema = {
    value: 'token',
};
const getFeedbackUserTokenResponseSchema = {
    ...genericResponseSchema,
    token: {
        path: 'data',
        fn: morphism(feedbackUserTokenSchema),
    },
};
const getPickABuyerLockResponseSchema = {
    ...genericResponseSchema,
    locked: 'data.locked',
};

class AidosClient {
    httpClient;
    constructor(baseUrl, timeout) {
        this.httpClient = clientFactory(baseUrl, '/v1', undefined, timeout);
    }
    /**
     * @param bottegaCookie - this arg must be passed in the full header form 'bottega=xxxxxxxxx'
     */
    async getFeedbackUserToken() {
        const [err, resp] = await to(this.httpClient.post(`/trust/feedback-api/tokens/user`, undefined, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getFeedbackUserTokenResponseSchema);
    }
    async getPickABuyerLock(adId) {
        const [err, resp] = await to(this.httpClient.get(`/pickabuyer/items/${adId}`));
        return handleResponse(err, resp, getPickABuyerLockResponseSchema);
    }
}

const UserFavoritesNotificationsPreferenceSchema = {
    userID: 'user_id',
    enabled: 'enabled',
};
const getUserFavoritesNotificationPreferenceResponseSchema = {
    ...genericResponseSchema,
    userPreference: {
        path: 'data',
        fn: morphism(UserFavoritesNotificationsPreferenceSchema),
    },
};

class AngeliaClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeout) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1/favorites/users', channel, timeout);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async getFavoriteNotificationPreference(userId) {
        const [err, rawResp] = await to(this.httpClient.get(`/${userId}/preferences/system_message`));
        return handleResponse(err, rawResp, getUserFavoritesNotificationPreferenceResponseSchema);
    }
    async updateFavoriteNotificationPreference(userId, enabled) {
        const [err, rawResp] = await to(this.httpClient.put(`/${userId}/preferences/system_message`, {
            enabled,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, rawResp, getUserFavoritesNotificationPreferenceResponseSchema);
    }
}

const baseGeoSchema$1 = {
    id: 'key',
    value: 'value',
    label: 'label',
    level: 'level',
};
const withFriendlyNameSchema = {
    friendlyName: 'friendly_name',
};
const regionSchema = {
    ...baseGeoSchema$1,
    ...withFriendlyNameSchema,
    neighbors: 'neighbors',
    uri: () => '/geo/region',
};
const citySchema = {
    ...baseGeoSchema$1,
    ...withFriendlyNameSchema,
    istat: 'istat',
    regionId: 'region_id',
    shortName: 'short_name',
    uri: () => '/geo/city',
};
const townSchema = {
    ...baseGeoSchema$1,
    ...withFriendlyNameSchema,
    istat: 'istat',
    cityId: 'city_id',
    hasZone: 'has_zone',
    regionId: 'region_id',
    uri: () => '/geo/town',
};
const zoneSchema = {
    ...baseGeoSchema$1,
    cityId: 'city_id',
    regionId: 'region_id',
    townId: 'town_id',
    uri: () => '/geo/zone',
};
const zipCodeSchema = {
    zipCode: 'zip_code',
    town: 'town',
    province: 'province',
};

const mapSchema = {
    address: 'address',
    latitude: 'latitude',
    longitude: 'longitude',
    zoom: 'zoom',
    showPin: {
        path: 'show_pin',
        // the non-strict equal is intentional
        fn: (showPin) => showPin == '1',
    },
};
const searchExtrasSchema = {
    advertiserTypeFacets: 'advertiser_facet.advertiser_type',
};
const itemCategorySchema = {
    id: 'key',
    label: 'value',
    parentId: 'macrocategory_id',
    friendlyName: 'friendly_name',
    weight: 'weight',
};
const itemAdvertiserSchema = {
    name: 'name',
    type: 'type',
    phone: 'phone',
    userId: 'user_id',
    company: 'company',
    shopId: 'shop_id',
    shopName: 'shop_name',
    proTransactionsEnabled: 'pro_transactions_enabled',
};
const itemGeoSchema$2 = createSchema({
    uri: 'uri',
    type: 'type',
    label: 'label',
    region: {
        path: 'region',
        fn: morphism(regionSchema),
    },
    city: {
        path: 'city',
        fn: morphism(citySchema),
    },
    town: {
        path: 'town',
        fn: morphism(townSchema),
    },
    map: {
        path: 'map',
        fn: morphism(mapSchema),
    },
}, { undefinedValues: { strip: true } });
const itemVisibilityOptionsSchema = {
    gallery: 'gallery',
    urgent: 'urgent',
    smartbumpedAt: 'smartbumped_at',
};

const itemImageSchema = {
    cdnBaseUrl: 'cdn_base_url',
};
const itemSchema = {
    kind: () => 'AdItem',
    urn: 'urn',
    type: 'type',
    category: {
        path: 'category',
        fn: morphism(itemCategorySchema),
    },
    subject: 'subject',
    body: 'body',
    date: 'dates.display',
    images: {
        path: 'images',
        fn: morphism(itemImageSchema),
    },
    features: {
        path: 'features',
        fn: (features) => features.reduce((feats, rawFeature) => ({
            ...feats,
            [rawFeature.uri]: rawFeature,
        }), {}),
    },
    advertiser: {
        path: 'advertiser',
        fn: morphism(itemAdvertiserSchema),
    },
    geo: {
        path: 'geo',
        fn: morphism(itemGeoSchema$2),
    },
    visibilityOptions: {
        path: 'visibility_options',
        fn: (visibility_options) => visibility_options
            ? morphism(itemVisibilityOptionsSchema, visibility_options)
            : undefined,
    },
    urls: 'urls',
};
const searchDataSchema = createSchema({
    total: 'count_all',
    checkNew: 'checknew',
    lines: 'lines',
    start: 'start',
    ads: {
        path: 'ads',
        fn: morphism(itemSchema),
    },
    filterLabels: 'filters',
    extras: {
        path: 'extra',
        fn: morphism(searchExtrasSchema),
    },
}, { undefinedValues: { strip: true } });
const searchResponseSchema$1 = {
    ...genericResponseSchema,
    search: {
        path: 'data',
        fn: morphism(searchDataSchema),
    },
};
const getConfResponseSchema = {
    ...genericResponseSchema,
    conf: 'data',
};
const getValuesResponseSchema = {
    ...genericResponseSchema,
    values: 'data.values',
    topValues: 'data.top_values',
};
const recommendedSearchSchema = {
    count: 'count',
    filters: 'filters',
};
const recommendedSearchResponseSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: morphism(recommendedSearchSchema),
    },
};

const defaultGalleryLimit = 3;
const uriPrefixRegex = /^\/v1\/values/;
class AnubisClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeout) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1', channel, timeout);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async search(params, options, environmentId) {
        let searchPath = '/search/items';
        if (options && 'gallerized' in options && options.gallerized) {
            searchPath = `${searchPath}/gallerized`;
            params.lim = options.galleryLimit || defaultGalleryLimit;
        }
        else if (options && 'geoboosted' in options && options.geoboosted) {
            searchPath = `${searchPath}/geoboosted`;
            params.lim = options.limit;
        }
        const headers = environmentId != undefined
            ? {
                'X-Subito-Environment-ID': environmentId,
            }
            : undefined;
        const [err, rawResp] = await to(this.httpClient.get(searchPath, {
            params,
            headers,
        }));
        const parsedResp = handleResponse(err, rawResp, searchResponseSchema$1);
        if (parsedResp.search &&
            options &&
            'gallerized' in options &&
            options?.gallerized) {
            for (let index = 0; index < parsedResp.search.ads.length; index++) {
                parsedResp.search.ads[index].gallerized = true;
            }
        }
        return parsedResp;
    }
    async getConf(params) {
        const confPath = '/search/items/conf';
        const [err, rawResp] = await to(this.httpClient.get(confPath, { params }));
        return handleResponse(err, rawResp, getConfResponseSchema);
    }
    async getValues(uri, params = {}) {
        if (!uri || !uri.startsWith('/v1/values')) {
            throw new Error('Missing or invalid URI parameter');
        }
        const parsedUri = uri.replace(uriPrefixRegex, '/values');
        const [err, resp] = await to(this.httpClient.get(parsedUri, { params }));
        return handleResponse(err, resp, getValuesResponseSchema);
    }
    async getSingleAd(listId, environmentId) {
        if (!listId) {
            throw new Error('Missing or invalid URN parameter');
        }
        const headers = environmentId != undefined
            ? {
                'X-Subito-Environment-ID': environmentId,
                'X-Subito-Canary-Support': 'phone_number_mask',
            }
            : undefined;
        const searchPath = `/search/items`;
        const [err, rawResp] = await to(this.httpClient.get(searchPath, {
            params: {
                urn: `list:${listId}`,
            },
            headers,
        }));
        const parsedResp = handleResponse(err, rawResp, searchResponseSchema$1);
        if (parsedResp.search?.total === 1) {
            return {
                ...parsedResp,
                ad: parsedResp.search.ads[0],
            };
        }
        else {
            return notFoundResponseError;
        }
    }
    async getRecommendedSearch(urn) {
        const [err, rawResp] = await to(this.httpClient.get('/search/items/pro/recommender', {
            params: {
                urn,
            },
        }));
        const parsedResp = handleResponse(err, rawResp, recommendedSearchResponseSchema);
        return parsedResp;
    }
}

const getCategorySuggestionsSchema = {
    ...genericResponseSchema,
    suggestions: 'data.suggestions',
};

class ApolloClient {
    httpClient;
    constructor(baseUrl, apiPath, channel) {
        this.httpClient = clientFactory(baseUrl, apiPath, channel);
    }
    async getCategorySuggestions(params) {
        const path = `/suggestions/categories`;
        const [err, rawResp] = await to(this.httpClient.get(path, {
            params,
        }));
        return handleResponse(err, rawResp, getCategorySuggestionsSchema);
    }
}

const paymentMethodCardSchema = {
    bin: 'bin',
    expirationMonth: 'expiration_month',
    expirationYear: 'expiration_year',
    last4: 'last4',
    type: 'type',
};
const paymentMethodSchema = createSchema({
    token: 'token',
    type: 'type',
    imageUrl: 'image_url',
    isDefault: 'default',
    account: 'account',
    card: {
        path: 'card',
        fn: morphism(paymentMethodCardSchema),
    },
}, { undefinedValues: { strip: true } });
const getPaymentMethodsResponseSchema = {
    ...genericResponseSchema,
    items: {
        path: 'data.items',
        fn: morphism(paymentMethodSchema),
    },
};

class AresClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeoutMs = 500) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1', channel, timeoutMs);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async getPaymentMethods() {
        const [err, resp] = await to(this.httpClient.get(`/payments/methods`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPaymentMethodsResponseSchema);
    }
    async removePaymentMethod(token) {
        const [err, resp] = await to(this.httpClient.delete(`/payments/methods/${token}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async setPaymentMethod(token) {
        const [err, resp] = await to(this.httpClient.put(`/payments/methods/${token}/default`, { default: true }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

const productSchema = {
    description: 'description',
    numMonth: 'num_month',
};
const subscriptionsSchema$1 = {
    status: 'status',
    startDate: 'start_date',
    endDate: 'end_date',
    paymentType: 'payment_type',
    totalPayment: 'total_payment',
    product: {
        path: 'product',
        fn: morphism(productSchema),
    },
};
const privateInfoSchema$1 = {
    name: 'name',
    surname: 'surname',
    fiscalCode: 'fiscal_code',
};
const companyInfoSchema$1 = {
    companyName: 'company_name',
    vatNumber: 'vat_number',
    taxCode: 'tax_code',
};
const billingInfoSchema$1 = {
    isCompany: 'is_company',
    town: 'town',
    city: 'city',
    postCode: 'postcode',
    address: 'address',
    privateInfo: {
        path: 'private_info',
        fn: morphism(privateInfoSchema$1),
    },
    companyInfo: {
        path: 'company_info',
        fn: morphism(companyInfoSchema$1),
    },
};
const userInfoSchema$1 = {
    activationDate: 'activation_date',
    signupDate: 'signup_date',
    email: 'email',
    status: 'status',
    changePasswordDate: 'change_pwd_date',
    phoneNumber: 'phone_number',
    phoneVerified: 'phone_verified',
    birthDate: 'birth_date',
    gender: 'gender',
    name: 'name',
    changePasswordIp: 'change_pwd_ip',
};
const trustInfoSchema = {
    reviewCount: 'review_count',
    score: 'score',
};
const accountInfoSchema = {
    userID: 'user_id',
    userInfo: {
        path: 'user_info',
        fn: morphism(userInfoSchema$1),
    },
    billingInfo: {
        path: 'billing_info',
        fn: morphism(billingInfoSchema$1),
    },
    subscriptions: {
        path: 'subscriptions',
        fn: morphism(subscriptionsSchema$1),
    },
    trustInfo: {
        path: 'trust_info',
        fn: morphism(trustInfoSchema),
    },
};
const getAccountsInfoResponseSchema = {
    ...genericResponseSchema,
    accountsInfo: {
        path: 'data',
        fn: morphism(accountInfoSchema),
    },
};

class AtroposClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1/private', channel);
    }
    async getAccounts(filterType, filterValue, secondaryFilters) {
        const [err, resp] = await to(this.httpClient.get(`/accounts`, {
            params: {
                filter_type: filterType,
                filter_value: filterValue,
                ...secondaryFilters,
            },
            withCredentials: true,
        }));
        return handleResponse(err, resp, getAccountsInfoResponseSchema);
    }
}

const factorsSchema = {
    availableFactors: 'available_factors',
    defaultFactor: 'default',
};
const factorsResponseSchema = {
    ...genericResponseSchema,
    factors: {
        path: 'data',
        fn: morphism(factorsSchema),
    },
};
const challengeSchema = {
    remainingAttempts: 'remaining_attempts',
    recipient: 'recipient',
};
const twoFactorChallengeResponseSchema = {
    ...genericResponseSchema,
    challenge: {
        path: 'data',
        fn: morphism(challengeSchema),
    },
};

class CacusClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeout) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1/challenges', channel, timeout);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async getFactors(token) {
        const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;
        const [err, resp] = await to(this.httpClient.get('/2fa/factors', { headers: authHeader }));
        return handleResponse(err, resp, factorsResponseSchema);
    }
    async challenge(challengeData, token) {
        const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;
        const [err, resp] = await to(this.httpClient.post('/2fa/challenge', challengeData, {
            headers: authHeader,
        }));
        return handleResponse(err, resp, twoFactorChallengeResponseSchema);
    }
    async verify(verifyData, token) {
        const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;
        const [err, resp] = await to(this.httpClient.post('/2fa/verify', verifyData, {
            headers: authHeader,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

const adInfoImageSchema = {
    baseUrl: 'base_url',
    cdnBaseUrl: 'cdn_base_url',
    uri: 'uri',
};
const adInfoSchema = createSchema({
    urn: 'urn',
    title: 'title',
    category: 'category',
    price: 'price',
    image: {
        path: 'image',
        fn: morphism(adInfoImageSchema),
    },
}, { undefinedValues: { strip: true } });
const shippingAddressInfoSchema = createSchema({
    destination: {
        path: 'destination',
        fn: morphism(userAddressSchema),
    },
    destinationServicePoint: {
        path: 'destination_service_point',
        fn: morphism(servicePointRecapSchema),
    },
    destinationType: 'destination_type',
    supportedDestinationTypes: 'supported_destination_types',
    shippingOptionID: 'shipping_option_id',
    shippingCarriers: 'shipping_carriers',
    origin: {
        path: 'origin',
        fn: morphism(userAddressSchema),
    },
}, { undefinedValues: { strip: true } });
const shippingStateActionSchema = {
    label: 'label',
    flowUrl: 'flow_url',
    style: 'style',
};
const stateSchema = {
    ID: 'id',
    buyerLabel: 'buyer_label',
    sellerLabel: 'seller_label',
    buyerHelpURL: 'buyer_help_url',
    sellerHelpURL: 'seller_help_url',
    style: 'style',
    options: 'options',
};
const getTransactionSchema = createSchema({
    transactionID: 'transaction_id',
    transactionType: 'transaction_type',
    buyerID: 'buyer_id',
    buyerName: 'buyer_name',
    sellerID: 'seller_id',
    sellerName: 'seller_name',
    state: 'state',
    actions: {
        path: 'actions',
        fn: morphism(shippingStateActionSchema),
    },
    itemId: 'item_id',
    itemPrice: 'item_price',
    adInfo: {
        path: 'ad_info',
        fn: morphism(adInfoSchema),
    },
    sellerRevenue: 'seller_revenue',
    shippingAddressInfo: {
        path: 'shipping_address_info',
        fn: morphism(shippingAddressInfoSchema),
    },
    updatedAt: 'updated_at',
    userType: 'user_type',
    buyerPrice: 'buyer_price',
}, { undefinedValues: { strip: true } });
const getTransactionsResponseSchema = {
    ...genericResponseSchema,
    transactions: {
        path: 'data.transactions',
        fn: morphism(getTransactionSchema),
    },
};
const transactionRecapSchema = {
    transactionID: 'transaction_id',
    transactionType: 'transaction_type',
    buyerID: 'buyer_id',
    sellerID: 'seller_id',
    state: 'state',
    itemId: 'item_id',
    itemPrice: 'item_price',
    createdAt: 'updated_at',
    updatedAt: 'updated_at',
    adTitle: 'ad_title',
};
const getTransactionsByMailSchema = {
    userID: 'user_id',
    email: 'email',
    transactions: {
        path: 'transactions',
        fn: morphism(transactionRecapSchema),
    },
};
const getTransactionsByMailResponseSchema = {
    ...genericResponseSchema,
    transactionsByMail: {
        path: 'data',
        fn: morphism(getTransactionsByMailSchema),
    },
};
const getTransactionResponseSchema = {
    ...genericResponseSchema,
    transaction: {
        path: 'data',
        fn: morphism(getTransactionSchema),
    },
};
const getStatesResponseSchema = {
    ...genericResponseSchema,
    states: {
        path: 'data.states',
        fn: morphism(stateSchema),
    },
};
const flowUrlSchema = {
    flowUrl: 'flow_url',
};
const getTransactionFlowUrlSchema = {
    ...genericResponseSchema,
    transactionFlow: {
        path: 'data',
        fn: morphism(flowUrlSchema),
    },
};
const getTransactionTimelineResponseSchema = {
    ...genericResponseSchema,
    timeline: 'data.timeline',
};
const pickupInfoSchema = {
    homeCollectionUrl: 'home_collection_url',
    collectionCenterUrl: 'collection_center_url',
    courierName: 'courier_name',
    trackingLink: 'tracking_link',
};
const getPickupInfoResponseSchema = {
    ...genericResponseSchema,
    pickupInfo: {
        path: 'data',
        fn: morphism(pickupInfoSchema),
    },
};
const pricePromoSchema = {
    promotionName: 'promotion_name',
    promotionDiscount: {
        path: 'promotion_discount',
        fn: morphism(PromoDiscountSchema),
    },
};
const priceItemSchema = {
    label: 'label',
    amount: 'amount',
    type: 'type',
    amountPrefixLabel: 'amount_prefix_label',
    strikethroughAmount: 'strikethrough_amount',
    promo: {
        path: 'promo',
        fn: morphism(pricePromoSchema),
    },
};
const pricesSchema = {
    total: 'total',
    item: {
        path: 'item',
        fn: morphism(priceItemSchema),
    },
    extra: {
        path: 'extra',
        fn: morphism(priceItemSchema),
    },
};
const getPricesResponseSchema = {
    ...genericResponseSchema,
    prices: {
        path: 'data',
        fn: morphism(pricesSchema),
    },
};
const returnSchema = {
    labelURL: 'label_url',
    trackingURL: 'tracking_url',
};
const createReturnResponseSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: morphism(returnSchema),
    },
};
const countSchema = {
    ...genericResponseSchema,
    payload: 'data.count',
};

class CaerusClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async getTransactions(role) {
        const [err, resp] = await to(this.httpClient.get(`/p2p/transactions`, {
            withCredentials: true,
            params: { role: role || 'buyer' },
        }));
        return handleResponse(err, resp, getTransactionsResponseSchema);
    }
    async getTransactionsByMail(email, adminJWT) {
        const [err, resp] = await to(this.httpClient.get(`/private/p2p/transactions`, {
            withCredentials: true,
            params: { email },
            headers: {
                Authorization: `Bearer ${adminJWT}`,
            },
        }));
        return handleResponse(err, resp, getTransactionsByMailResponseSchema);
    }
    async getTransaction(transactionType, transactionID) {
        const [err, resp] = await to(this.httpClient.get(`/p2p/transactions/${transactionType}/instances/${transactionID}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getTransactionResponseSchema);
    }
    async getTransactionStates() {
        const [err, resp] = await to(this.httpClient.get('/p2p/states'));
        return handleResponse(err, resp, getStatesResponseSchema);
    }
    async getPickupInfo(transactionType, transactionID) {
        const [err, resp] = await to(this.httpClient.get(`/p2p/transactions/${transactionType}/instances/${transactionID}/pickup/info`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPickupInfoResponseSchema);
    }
    async getPricesPreview(transactionType, transactionID, itemPrice) {
        let url = `/p2p/transactions/${transactionType}/instances/${transactionID}/prices/preview`;
        if (itemPrice) {
            url += `?item_price=${itemPrice}`;
        }
        const [err, resp] = await to(this.httpClient.get(url, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPricesResponseSchema);
    }
    async getPrices(transactionType, transactionID) {
        const [err, resp] = await to(this.httpClient.get(`/p2p/transactions/${transactionType}/instances/${transactionID}/prices`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPricesResponseSchema);
    }
    async getTransactionTimeline(transactionID) {
        const [err, resp] = await to(this.httpClient.get(`/p2p/transactions/${transactionID}/timeline`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getTransactionTimelineResponseSchema);
    }
    async getTransactionSurvey() {
        const [err, resp] = await to(this.httpClient.get(`/p2p/transactions/csat_survey`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async setTransactionSurveyCompleted() {
        const [err, resp] = await to(this.httpClient.put(`/p2p/transactions/csat_survey/complete`, undefined, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async requestShipment(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'request_shipment');
    }
    async acceptShipment(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'accept_shipment');
    }
    async refuseShipment(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'refuse_shipment');
    }
    async confirmShipment(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'confirm_shipment');
    }
    async cancelShipment(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'cancel');
    }
    async create(itemID, itemType, isBuyNow) {
        const body = {
            item_id: itemID,
            item_type: itemType,
        };
        if (isBuyNow !== undefined)
            body.cta = isBuyNow ? 'buy' : 'offer';
        const [err, resp] = await to(this.httpClient.post(`/p2p/transactions`, body, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getTransactionFlowUrlSchema);
    }
    async requestPurchase(transactionType, transactionID, itemPrice) {
        const [err, resp] = await to(this.httpClient.post(`/p2p/transactions/${transactionType}/instances/${transactionID}/actions/request_purchase`, { item_price: itemPrice }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getTransactionResponseSchema);
    }
    async sellerRequestPurchase(transactionType, transactionID, itemPrice) {
        const [err, resp] = await to(this.httpClient.post(`/p2p/transactions/${transactionType}/instances/${transactionID}/actions/seller_request_purchase`, { item_price: itemPrice }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getTransactionResponseSchema);
    }
    async openDispute(transactionType, transactionID, issueType, disputaData) {
        const [err, resp] = await to(this.httpClient.post(`/p2p/transactions/${transactionType}/instances/${transactionID}/open_dispute/${issueType}`, disputaData, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async cancelDispute(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'cancel_dispute');
    }
    async setDestinationType(transactionID, destinationType) {
        const [err, resp] = await to(this.httpClient.put(`/p2p/transactions/fullshipping/instances/${transactionID}/destination_type`, {
            destination_type: destinationType,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async setDestinationServicePoint(transactionID, servicePointID) {
        const [err, resp] = await to(this.httpClient.put(`/p2p/transactions/fullshipping/instances/${transactionID}/destination_service_point`, {
            carrier_id: servicePointID.carrierID,
            country_code: servicePointID.countryCode,
            service_point_id: servicePointID.servicePointID,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async setDestinationAddress(transactionType, transactionID, addressID) {
        const [err, resp] = await to(this.httpClient.put(`/p2p/transactions/${transactionType}/instances/${transactionID}/destination_address/${addressID}`, undefined, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async setOriginAddress(transactionType, transactionID, addressID) {
        const [err, resp] = await to(this.httpClient.put(`/p2p/transactions/${transactionType}/instances/${transactionID}/origin_address/${addressID}`, undefined, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async checkLabel(transactionType, transactionID) {
        const [err, resp] = await to(this.httpClient.head(`/p2p/transactions/${transactionType}/instances/${transactionID}/shipping/label`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async createReturn(transactionType, transactionID, adminJWT) {
        const [err, resp] = await to(this.httpClient.post(`/private/p2p/transactions/${transactionType}/instances/${transactionID}/actions/return_label`, undefined, {
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${adminJWT}`,
            },
        }));
        return handleResponse(err, resp, createReturnResponseSchema);
    }
    async cancelTransaction(transactionType, transactionID, adminJWT) {
        const [err, resp] = await to(this.httpClient.post(`/p2p/transactions/${transactionType}/instances/${transactionID}/actions/admin_cancel`, undefined, {
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${adminJWT}`,
            },
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async bulkAdminCancel(adminJWT, userID, dryRun = true) {
        const [err, resp] = await to(this.httpClient.post(`/p2p/users/${userID}/actions/bulk_admin_cancel`, undefined, {
            params: { dry_run: dryRun },
            headers: {
                Authorization: `Bearer ${adminJWT}`,
            },
        }));
        return handleResponse(err, resp, countSchema);
    }
    async refusePurchase(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'refuse_purchase');
    }
    async cancelRequestPurchase(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'cancel');
    }
    async cancelSell(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'seller_cancel');
    }
    async acceptPurchase(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'accept_purchase');
    }
    async confirmAvailability(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'confirm_availability');
    }
    async confirmReturn(transactionType, transactionID) {
        return this.changeState(transactionType, transactionID, 'confirm_return');
    }
    async closePurchase(transactionType, transactionID, headers) {
        return this.changeState(transactionType, transactionID, 'close', headers);
    }
    async adminClose(transactionType, transactionID, adminJWT) {
        const [err, resp] = await to(this.httpClient.post(`/p2p/transactions/${transactionType}/instances/${transactionID}/actions/close`, undefined, {
            withCredentials: true,
            headers: {
                Authorization: `Bearer ${adminJWT}`,
            },
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async changeState(transactionType, transactionID, action, headers) {
        const [err, resp] = await to(this.httpClient.post(`/p2p/transactions/${transactionType}/instances/${transactionID}/actions/${action}`, undefined, {
            withCredentials: true,
            headers,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

const dac7Schema = {
    firstName: 'name',
    lastName: 'surname',
    dateOfBirth: 'date_of_birth',
    countryOfBirth: 'place_of_birth',
    cityOfBirth: 'town_of_birth',
    country: 'country',
    town: 'town',
    city: 'city',
    address: 'address',
    postalCode: 'postal_code',
    taxCode: 'fiscal_code',
    iban: {
        path: 'receiving_payment_methods.iban',
        fn: (value) => value ?? [''],
    },
    paypal: {
        path: 'receiving_payment_methods.paypal',
        fn: (value) => value ?? [''],
    },
    disclaimer: 'user_certified',
};
const getDac7ResponseSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: morphism(dac7Schema),
    },
};

const dac7RequestSchema = {
    name: 'firstName',
    surname: 'lastName',
    date_of_birth: 'dateOfBirth',
    place_of_birth: 'countryOfBirth',
    town_of_birth: 'cityOfBirth',
    country: 'country',
    town: 'town',
    city: 'city',
    address: 'address',
    postal_code: 'postalCode',
    fiscal_code: 'taxCode',
    receiving_payment_methods: (values) => ({
        iban: isLikeEmptyArray(values.iban) ? null : values.iban,
        paypal: isLikeEmptyArray(values.paypal) ? null : values.paypal,
    }),
    user_certified: 'disclaimer',
};
function isLikeEmptyArray(array) {
    return array?.every((v) => !v);
}

class ChrysusClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async getDAC7(bottegaCookieHeader, userId) {
        const [err, resp] = await to(this.httpClient.get(`/users/${userId}/dac7`, {
            withCredentials: true,
            headers: {
                Cookie: bottegaCookieHeader,
            },
        }));
        return handleResponse(err, resp, getDac7ResponseSchema);
    }
    async updateDAC7(userId, data, bottegaCookieHeader) {
        const body = morphism(dac7RequestSchema, data);
        const config = {
            withCredentials: true,
        };
        if (bottegaCookieHeader) {
            config.headers = {
                Cookie: bottegaCookieHeader,
            };
        }
        const [err, resp] = await to(this.httpClient.put(`/users/${userId}/dac7`, body, config));
        return handleResponse(err, resp, getDac7ResponseSchema);
    }
}

const adSchema$1 = {
    adID: 'ad_id',
    advertiserType: 'advertiser_type',
    body: 'body',
    categoryID: 'category_id',
    categoryName: 'category_name',
    channel: 'channel',
    city: 'city',
    cityID: 'city_id',
    companyAd: 'company_ad',
    deletedAt: 'deleted_at',
    deleteReason: 'delete_reason',
    email: 'email',
    images: 'images',
    listID: 'list_id',
    listTime: 'list_time',
    listingVersion: 'listing_version',
    listingTimestamp: 'listing_timestamp',
    name: 'name',
    adParams: 'ad_params',
    phone: 'phone',
    phoneHidden: 'phone_hidden',
    price: 'price',
    refuseReason: 'refuse_reason',
    refuseUser: 'refuse_user',
    region: 'region',
    regionID: 'region_id',
    remoteAddr: 'remote_addr',
    reviewResult: 'review_result',
    reviewVersion: 'review_version',
    reviewTimestamp: 'review_timestamp',
    status: 'status',
    statusTransitionTimestamp: 'status_transition_timestamp',
    subject: 'subject',
    originalDate: 'orig_date',
    originalListTime: 'orig_list_time',
    type: 'type',
    updateTimestamp: 'update_timestamp',
    userID: 'user_id',
    version: 'version',
};
const adHistorySchema = {
    adID: 'ad_id',
    eventType: 'event_type',
    producedAt: 'produced_at',
    version: 'version',
    admin: 'admin',
    refusalReason: 'refusal_reason',
    remoteAddress: 'remote_address',
};
const aggregationsSchema = {
    key: 'key',
    docCount: 'doc_count',
};
const getAdByIDResponseSchema = {
    ...genericResponseSchema,
    ads: {
        path: 'data',
        fn: morphism(adSchema$1),
    },
};
const searchAdsResponseSchema = {
    ...genericResponseSchema,
    hits: 'data.hits',
    pageNumber: 'data.page_number',
    totalPages: 'data.total_pages',
    ads: {
        path: 'data.results',
        fn: morphism(adSchema$1),
    },
    aggregations: {
        path: 'data.aggregations',
        fn: morphism(aggregationsSchema),
    },
};
const getAdHistoryResponseSchema = {
    ...genericResponseSchema,
    history: {
        path: 'data',
        fn: morphism(adHistorySchema),
    },
};

const snapshotSchema = {
    adID: 'ad_id',
    version: 'version',
    versionDate: 'version_date',
    snapshot: {
        path: 'content',
        fn: morphism(adSchema$1),
    },
};
const getSnapshotsResponseSchema = {
    ...genericResponseSchema,
    snapshots: {
        path: 'data',
        fn: morphism(snapshotSchema),
    },
};

class CloacinaClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '', channel);
    }
    async getSnapshot(adID) {
        const [err, resp] = await to(this.httpClient.get(`/private/snapshot/${adID}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getSnapshotsResponseSchema);
    }
}

const calltrackerUserInfoDataSchema = {
    phone: 'calltracker_phone_number',
    userPhone: 'user_phone_number',
};
const contactInfoSchema = {
    adURN: 'ad_urn',
    phoneNumber: 'phone_number',
    force: 'force',
};
const calltrackerUserSchema = {
    userID: 'user_id',
    email: 'email',
    categories: 'calltracker_categories',
    disabled: 'force_ct_disabled',
    info: {
        path: 'contact_info',
        fn: morphism(calltrackerUserInfoDataSchema),
    },
};
const getContactInfoResponseSchema = {
    ...genericResponseSchema,
    contactInfo: {
        path: 'data',
        fn: morphism(contactInfoSchema),
    },
};
const getUserResponseScherma = {
    ...genericResponseSchema,
    user: {
        path: 'data',
        fn: morphism(calltrackerUserSchema),
    },
};

class ClothoClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1/contacts/ads', channel);
    }
    async getContactInfo(adURN) {
        const [err, resp] = await to(this.httpClient.get(`/${adURN}`));
        return handleResponse(err, resp, getContactInfoResponseSchema);
    }
}

class ClothoBackOfficeClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1/private/calltracker', channel);
    }
    async getUser(email, jwt) {
        const headers = jwt
            ? {
                Authorization: `Bearer ${jwt}`,
            }
            : undefined;
        const [err, resp] = await to(this.httpClient.get(`/users?email=${email}`, { headers }));
        return handleResponse(err, resp, getUserResponseScherma);
    }
    async resetUser(userID, jwt) {
        return this.actionsHandler(userID, jwt, 'reset');
    }
    async enableUser(userID, jwt) {
        return this.actionsHandler(userID, jwt, 'enable');
    }
    async disableUser(userID, jwt) {
        return this.actionsHandler(userID, jwt, 'disable');
    }
    async actionsHandler(userID, jwt, action) {
        const headers = jwt
            ? {
                Authorization: `Bearer ${jwt}`,
            }
            : undefined;
        const [err, resp] = await to(this.httpClient.post(`/users/${userID}/${action}`, undefined, {
            withCredentials: true,
            headers,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

var CategoryId;
(function (CategoryId) {
    CategoryId["Tutte"] = "0";
    CategoryId["Motori"] = "1";
    CategoryId["Auto"] = "2";
    CategoryId["AccessoriAuto"] = "5";
    CategoryId["Moto"] = "3";
    CategoryId["AccessoriMoto"] = "36";
    CategoryId["Nautica"] = "22";
    CategoryId["CaravanECamper"] = "34";
    CategoryId["VeicoliCommerciali"] = "4";
    CategoryId["Immobili"] = "6";
    CategoryId["Appartamenti"] = "7";
    CategoryId["CamerePostiLetto"] = "43";
    CategoryId["Ville"] = "29";
    CategoryId["TerreniRustici"] = "30";
    CategoryId["GarageBox"] = "31";
    CategoryId["LoftMansarde"] = "32";
    CategoryId["CaseVacanza"] = "33";
    CategoryId["UfficiLocali"] = "8";
    CategoryId["LavoroServizi"] = "24";
    CategoryId["OfferteLavoro"] = "26";
    CategoryId["Servizi"] = "50";
    CategoryId["Candidati"] = "42";
    CategoryId["Attrezzature"] = "25";
    CategoryId["Elettronica"] = "9";
    CategoryId["Informatica"] = "10";
    CategoryId["ConsoleVideogiochi"] = "44";
    CategoryId["AudioVideo"] = "11";
    CategoryId["Fotografia"] = "40";
    CategoryId["Telefonia"] = "12";
    CategoryId["CasaPersona"] = "13";
    CategoryId["ArredamentoCasalinghi"] = "14";
    CategoryId["Elettrodomestici"] = "37";
    CategoryId["GiardinoFaiDaTe"] = "15";
    CategoryId["AbbigliamentoAccessori"] = "16";
    CategoryId["TuttoBambini"] = "17";
    CategoryId["SportsHobby"] = "18";
    CategoryId["Animali"] = "23";
    CategoryId["AccessoriAnimali"] = "100";
    CategoryId["MusicaFilm"] = "19";
    CategoryId["LibriRiviste"] = "38";
    CategoryId["StrumentiMusicali"] = "39";
    CategoryId["Sports"] = "20";
    CategoryId["Biciclette"] = "41";
    CategoryId["Collezionismo"] = "21";
    CategoryId["Altri"] = "27";
    CategoryId["AltriAnnunci"] = "28";
})(CategoryId || (CategoryId = {}));
const isCategory = (cat) => cat.categories === undefined;
const isMacroCategory = (cat) => cat.categories !== undefined;
class MacroCategoryStore {
    /**
     * @deprecated use CategoryStore in [@sbt-web/utils](https://github.mpi-internal.com/SCM-Italy/sbt-web/blob/master/packages/utils/src/categoryHelper/categoryStore.ts#L10
     */
    macroCategories = [];
    /**
     * @deprecated use CategoryStore in @sbt-web/utils
     */
    getAllCategories() {
        return this.macroCategories.reduce((list, cat) => {
            return list.concat(cat).concat(cat.categories);
        }, []);
    }
    /**
     * @deprecated use CategoryStore in @sbt-web/utils
     */
    getCategoryById(id) {
        return this._search((cat) => cat.id === id);
    }
    /**
     * @deprecated use CategoryStore in @sbt-web/utils
     */
    getCategoryByFriendly(friendly) {
        return this._search((cat) => cat.friendly === friendly);
    }
    /**
     * @deprecated use CategoryStore in [@sbt-web/utils](https://github.mpi-internal.com/SCM-Italy/sbt-web/blob/master/packages/utils/src/categoryHelper/categoryStore.ts#L10
     */
    getLabelById(id, options = {}) {
        const cat = this._search((cat) => cat.id === id);
        let label = cat.label;
        if (options.friendly) {
            label = cat.friendly;
        }
        return label;
    }
    /**
     * @deprecated use CategoryStore in [@sbt-web/utils](https://github.mpi-internal.com/SCM-Italy/sbt-web/blob/master/packages/utils/src/categoryHelper/categoryStore.ts#L10
     */
    getMacroByCategory(cat) {
        return this.getMacroByCategoryId(cat.id);
    }
    /**
     * @deprecated use CategoryStore in [@sbt-web/utils](https://github.mpi-internal.com/SCM-Italy/sbt-web/blob/master/packages/utils/src/categoryHelper/categoryStore.ts#L10
     * Finds the macro category of a leaf category. If given the ID of a macro,
     * returns undefined. This is an upward-only search.
     * @param id The ID of the leaf category.
     * @returns The Macro of the leaf, or undefined if there is no parent (including if the category is already a macro.)
     */
    getMacroByCategoryId(id) {
        let found = undefined;
        this.macroCategories.some((macro) => {
            if (macro.categories.find((c) => c.id === id)) {
                found = macro;
                return true;
            }
            return false;
        });
        return found;
    }
    _search(condition) {
        let category = this.macroCategories[0];
        this.macroCategories.some((macro) => {
            if (condition(macro)) {
                category = macro;
                return true;
            }
            const found = macro.categories.find(condition);
            if (found) {
                category = found;
                return true;
            }
            return false;
        });
        return category;
    }
}

// This category doesn't exist in the backend but we need it here to generate the filters ui
const category0 = {
    identifier: '0',
    friendly_name: 'usato',
    label: 'Tutte le categorie',
    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
    default_ad_type: 's',
    ad_types: ['s', 'g', 'u', 'h', 'k'],
    categories: [],
};
const generatedConfigurations = {
    macrocategories: [
        {
            identifier: '1',
            friendly_name: 'motori',
            label: 'MOTORI',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'k'],
            categories: [
                {
                    identifier: '2',
                    friendly_name: 'auto',
                    label: 'Auto',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: [
                        'datedesc',
                        'relevance',
                        'priceasc',
                        'pricedesc',
                        'yearasc',
                        'yeardesc',
                        'mileageasc',
                        'mileagedesc',
                    ],
                },
                {
                    identifier: '5',
                    friendly_name: 'accessori-auto',
                    label: 'Accessori Auto',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '3',
                    friendly_name: 'moto-e-scooter',
                    label: 'Moto e Scooter',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: [
                        'datedesc',
                        'relevance',
                        'priceasc',
                        'pricedesc',
                        'yearasc',
                        'yeardesc',
                        'mileageasc',
                        'mileagedesc',
                    ],
                },
                {
                    identifier: '36',
                    friendly_name: 'accessori-moto',
                    label: 'Accessori Moto',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '22',
                    friendly_name: 'nautica',
                    label: 'Nautica',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '34',
                    friendly_name: 'caravan-e-camper',
                    label: 'Caravan e Camper',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '4',
                    friendly_name: 'veicoli-commerciali',
                    label: 'Veicoli commerciali',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '6',
            friendly_name: 'immobili',
            label: 'IMMOBILI',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'k', 'u', 'h'],
            categories: [
                {
                    identifier: '7',
                    friendly_name: 'appartamenti',
                    label: 'Appartamenti',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '43',
                    friendly_name: 'camere-posti-letto',
                    label: 'Camere/Posti letto',
                    default_ad_type: 'u',
                    ad_types: ['k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '29',
                    friendly_name: 'ville-singole-e-a-schiera',
                    label: 'Ville singole e a schiera',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '30',
                    friendly_name: 'terreni-e-rustici',
                    label: 'Terreni e rustici',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '31',
                    friendly_name: 'garage-e-box',
                    label: 'Garage e box',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '32',
                    friendly_name: 'loft-mansarde',
                    label: 'Loft, mansarde e altro',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '33',
                    friendly_name: 'case-vacanza',
                    label: 'Case vacanza',
                    default_ad_type: 'h',
                    ad_types: ['k', 'h'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
                {
                    identifier: '8',
                    friendly_name: 'uffici-locali-commerciali',
                    label: 'Uffici e Locali commerciali',
                    default_ad_type: 's',
                    ad_types: ['s', 'k', 'u'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                    supports_city_zones: true,
                },
            ],
        },
        {
            identifier: '24',
            friendly_name: 'lavoro',
            label: 'LAVORO E SERVIZI',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s'],
            categories: [
                {
                    identifier: '26',
                    friendly_name: 'offerte-lavoro',
                    label: 'Offerte di lavoro',
                    default_ad_type: 's',
                    ad_types: ['s'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '50',
                    friendly_name: 'servizi',
                    label: 'Servizi',
                    default_ad_type: 's',
                    ad_types: ['s'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '42',
                    friendly_name: 'cerco-lavoro',
                    label: 'Candidati in cerca di lavoro',
                    default_ad_type: 's',
                    ad_types: ['s'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '25',
                    friendly_name: 'attrezzature',
                    label: 'Attrezzature di lavoro',
                    default_ad_type: 's',
                    ad_types: ['s', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '9',
            friendly_name: 'elettronica',
            label: 'ELETTRONICA',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '10',
                    friendly_name: 'informatica',
                    label: 'Informatica',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '44',
                    friendly_name: 'videogiochi',
                    label: 'Console e Videogiochi',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '11',
                    friendly_name: 'audio-video',
                    label: 'Audio/Video',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '40',
                    friendly_name: 'fotografia',
                    label: 'Fotografia',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '12',
                    friendly_name: 'telefonia',
                    label: 'Telefonia',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '13',
            friendly_name: 'casa-e-persona',
            label: 'PER LA CASA e LA PERSONA',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '14',
                    friendly_name: 'arredamento-casalinghi',
                    label: 'Arredamento e Casalinghi',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '37',
                    friendly_name: 'elettrodomestici',
                    label: 'Elettrodomestici',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '15',
                    friendly_name: 'giardino-fai-da-te',
                    label: 'Giardino e Fai da te',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '16',
                    friendly_name: 'abbigliamento-accessori',
                    label: 'Abbigliamento e Accessori',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '17',
                    friendly_name: 'bambini-giocattoli',
                    label: 'Tutto per i bambini',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '18',
            friendly_name: 'sport-hobby',
            label: 'SPORTS E HOBBY',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '23',
                    friendly_name: 'animali',
                    label: 'Animali',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '100',
                    friendly_name: 'accessori-per-animali',
                    label: 'Accessori per animali',
                    default_ad_type: 's',
                    ad_types: ['s', 'g'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '19',
                    friendly_name: 'musica-film',
                    label: 'Musica e Film',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '38',
                    friendly_name: 'libri-riviste',
                    label: 'Libri e Riviste',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '39',
                    friendly_name: 'strumenti-musicali',
                    label: 'Strumenti Musicali',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '20',
                    friendly_name: 'sport',
                    label: 'Sports',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '41',
                    friendly_name: 'biciclette',
                    label: 'Biciclette',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
                {
                    identifier: '21',
                    friendly_name: 'hobby-collezionismo',
                    label: 'Collezionismo',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['relevance', 'datedesc', 'priceasc', 'pricedesc'],
                },
            ],
        },
        {
            identifier: '27',
            friendly_name: 'vari',
            label: '--',
            sorting_list: ['datedesc', 'priceasc', 'pricedesc'],
            default_ad_type: 's',
            ad_types: ['s', 'g', 'k'],
            categories: [
                {
                    identifier: '28',
                    friendly_name: 'annunci-vari',
                    label: 'Altri',
                    default_ad_type: 's',
                    ad_types: ['s', 'g', 'k'],
                    sorting_list: ['datedesc', 'relevance', 'priceasc', 'pricedesc'],
                },
            ],
        },
    ],
};
const configurations = {
    macrocategories: [category0, ...generatedConfigurations.macrocategories],
};

const categorySchema = {
    id: 'identifier',
    label: 'label',
    friendly: 'friendly_name',
    orders: 'sorting_list',
    adTypes: 'ad_types',
    defaultAdType: 'default_ad_type',
    zoneSupport: 'supports_city_zones',
};
const macroCategorySchema = {
    id: 'identifier',
    label: 'label',
    friendly: 'friendly_name',
    orders: 'sorting_list',
    adTypes: 'ad_types',
    defaultAdType: 'default_ad_type',
    categories: {
        path: 'categories',
        fn: morphism(categorySchema),
    },
};
const macroCatStoreSchema = {
    macroCategories: {
        path: 'macrocategories',
        fn: morphism(macroCategorySchema),
    },
    getAllCategories: '',
    getLabelById: '',
    getCategoryByFriendly: '',
    getCategoryById: '',
    getMacroByCategory: '',
    getMacroByCategoryId: '',
};
const CategoryStore = morphism(macroCatStoreSchema, configurations, MacroCategoryStore);

class DikeClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/', channel);
    }
    async postUndorefuse(ad_id, version, category, user) {
        const [err, resp] = await to(this.httpClient.post(`/v1/private/undorefuse`, {
            ad_id,
            version,
            category,
            user,
        }));
        return handleGenericResponse(err, resp);
    }
}

class DioneClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/', channel);
    }
    async getAdByID(ids) {
        const [err, resp] = await to(this.httpClient.get(`/ad/id`, {
            withCredentials: true,
            params: {
                ad_id: ids.join(','),
            },
        }));
        return handleResponse(err, resp, getAdByIDResponseSchema);
    }
    async searchAds(page, adID, email, status, listID, userID, remoteAddr, phone, channel) {
        const [err, resp] = await to(this.httpClient.get(`/ad/search`, {
            withCredentials: true,
            params: {
                page,
                ad_id: adID?.join(','),
                email: email?.join(','),
                status: status?.join(','),
                list_id: listID?.join(','),
                user_id: userID?.join(','),
                remote_addr: remoteAddr?.join(','),
                phone: phone?.join(','),
                channel: channel?.join(','),
            },
        }));
        return handleResponse(err, resp, searchAdsResponseSchema);
    }
    async getAdHistory(adId) {
        const [err, resp] = await to(this.httpClient.get(`/ad/history/${adId}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getAdHistoryResponseSchema);
    }
}

const adSchema = {
    adID: 'ad_id',
    reportedAt: 'reported_at',
    title: 'title',
    total: 'total',
};
const getAdsResponseSchema = {
    ...genericResponseSchema,
    ads: {
        path: 'data.reported_ads',
        fn: morphism(adSchema),
    },
};
const reportingSchema = {
    adID: 'ad_id',
    reportingID: 'reporting_id',
    reportedAt: 'reported_at',
    userEmail: 'user_email',
    reasonID: 'reason_id',
    description: 'description',
};
const getReportingResponseSchema = {
    ...genericResponseSchema,
    reportings: {
        path: 'data.reported_infos',
        fn: morphism(reportingSchema),
    },
};
const getDSAReportResponseSchema = {
    ...genericResponseSchema,
    reporterUserId: 'data.reporter_user_id',
    reportedUserId: 'data.reported_user_id',
    reportStatus: 'data.status',
    reportCreatedAt: 'data.report_created_at',
};

class DysnomiaClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async getAds(from) {
        let url = '/review-admin/complaints';
        if (from) {
            url = url.concat('/').concat(from.toISOString());
        }
        const [err, resp] = await to(this.httpClient.get(url, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getAdsResponseSchema);
    }
    async getAdsByReasons(reasons, from) {
        let url = '/review-admin/complaints-reasons/';
        if (from) {
            url = url.concat('time/').concat(from.toISOString()).concat('/reasons/');
        }
        url = url.concat(reasons.join(','));
        const [err, resp] = await to(this.httpClient.get(url, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getAdsResponseSchema);
    }
    async getReportings(adID) {
        const [err, resp] = await to(this.httpClient.get(`/review-admin/complaints/info/${adID}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getReportingResponseSchema);
    }
    async putReportingsByAdId(adID, review, admin) {
        const [err, resp] = await to(this.httpClient.put(`/review-admin/complaints/ad/${adID}`, {
            action_type: review,
            admin_id: admin,
        }));
        return handleGenericResponse(err, resp);
    }
    async putReportingsByReportingId(reportingID, review, admin) {
        const [err, resp] = await to(this.httpClient.put(`/review-admin/complaints/info/${reportingID}`, {
            action_type: review,
            admin_id: admin,
        }));
        return handleGenericResponse(err, resp);
    }
    async postComplaint(data, headers = {}) {
        const [err, resp] = await to(this.httpClient.post('/review/complaints', data, { headers }));
        return handleGenericResponse(err, resp);
    }
    async getReportDSA(reportingID) {
        const [err, resp] = await to(this.httpClient.get(`/review-admin/dsa/userreports/${reportingID}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getDSAReportResponseSchema);
    }
}

const tosSchema = {
    ...genericResponseSchema,
    tos: 'data.tos',
};

class EchoClient {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/v1/users', channel, timeout);
    }
    async getTos(userId, headers) {
        const [err, resp] = await to(this.httpClient.get(`/${userId}/info/tos`, {
            headers,
            withCredentials: true,
        }));
        return handleResponse(err, resp, tosSchema);
    }
    async saveTos(userId, tos, headers) {
        const [err, resp] = await to(this.httpClient.put(`/${userId}/info/tos`, { tos }, {
            headers,
            withCredentials: true,
        }));
        return handleResponse(err, resp, tosSchema);
    }
}

const getDAStartConversationResponseSchema = {
    ...genericResponseSchema,
};
const postDAContinueConversationResponseSchema = {
    ...genericResponseSchema,
};

class GaneshaClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async startConversation({ transactionId, transactionType, }) {
        const apiRequest = this.httpClient.get('/conversations', {
            withCredentials: true,
            params: {
                transaction_id: transactionId,
                transaction_type: transactionType,
            },
        });
        const [err, resp] = await to(apiRequest);
        return handleResponse(err, resp, getDAStartConversationResponseSchema);
    }
    async continueConversation({ transactionId, transactionType, actionId, messages, }) {
        const apiRequest = this.httpClient.post('/conversations', {
            transaction_id: transactionId,
            transaction_type: transactionType,
            action_id: actionId,
            messages,
        }, {
            withCredentials: true,
        });
        const [err, resp] = await to(apiRequest);
        return handleResponse(err, resp, postDAContinueConversationResponseSchema);
    }
}

const zoneResponseSchema = {
    ...genericResponseSchema,
    zones: {
        path: 'data.values',
        fn: morphism(zoneSchema),
    },
};
const citiesResponseSchema = {
    ...genericResponseSchema,
    region: 'data.region',
    cities: {
        path: 'data.values',
        fn: morphism(citySchema),
    },
};
const townsResponseSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: morphism(zipCodeSchema),
    },
};
const GeoEntrySchema = createSchema({
    region: {
        path: 'region',
        fn: morphism(regionSchema),
    },
    city: {
        path: 'city',
        fn: morphism(citySchema),
    },
    town: {
        path: 'town',
        fn: morphism(townSchema),
    },
}, { undefinedValues: { strip: true } });
const searchResponseSchema = {
    ...genericResponseSchema,
    entries: {
        path: 'data.data',
        fn: morphism(GeoEntrySchema),
    },
};

class GeogosClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1/geo', channel);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async search(key, query) {
        const [err, resp] = await to(this.httpClient.get('search', {
            params: { key, ...query },
        }));
        return handleResponse(err, resp, searchResponseSchema);
    }
    async getZones(town) {
        const [err, resp] = await to(this.httpClient.get(`regions/${town.regionId}/cities/${town.cityId}/towns/${town.id}/zones`));
        return handleResponse(err, resp, zoneResponseSchema);
    }
    async getCities(regionID) {
        const [err, resp] = await to(this.httpClient.get(`regions/${regionID}/cities`));
        return handleResponse(err, resp, citiesResponseSchema);
    }
    async getTowns(zipcode) {
        const [err, resp] = await to(this.httpClient.get(`search/zipcodes`, {
            params: {
                query: zipcode,
                invalidate_cache: 'true',
            },
        }));
        return handleResponse(err, resp, townsResponseSchema);
    }
}
var GeoLevel;
(function (GeoLevel) {
    GeoLevel[GeoLevel["Italia"] = 0] = "Italia";
    GeoLevel[GeoLevel["RegionAndNearRegions"] = 1] = "RegionAndNearRegions";
    GeoLevel[GeoLevel["Region"] = 2] = "Region";
    GeoLevel[GeoLevel["City"] = 3] = "City";
    GeoLevel[GeoLevel["Town"] = 4] = "Town";
})(GeoLevel || (GeoLevel = {}));
function getGeoLevel(geo, searchNearRegions) {
    if (!geo?.region?.value)
        return null;
    if (geo.town) {
        return GeoLevel.Town;
    }
    else if (geo.city) {
        return GeoLevel.City;
    }
    else if (!searchNearRegions && geo.region.id != TuttaItalia.id) {
        return GeoLevel.Region;
    }
    else if (searchNearRegions && geo.region.id != TuttaItalia.id) {
        return GeoLevel.RegionAndNearRegions;
    }
    return GeoLevel.Italia;
}
/**
 * Returns a geo label to be used when describing a specific geo entry
 *
 * e.g. 'Milano (MI)', 'Milano e provincia', 'Lombardia e regioni vicine'
 */
function getLabelFromGeo(geo, searchNearRegions) {
    const geoLevel = getGeoLevel(geo, searchNearRegions);
    switch (geoLevel) {
        case GeoLevel.Town:
            return `${geo.town?.value} ${geo.city?.shortName ? `(${geo.city?.shortName})` : ''}`;
        case GeoLevel.City:
            return `${geo.city?.value} e provincia`;
        case GeoLevel.Region:
            return `${geo.region?.value} regione`;
        case GeoLevel.RegionAndNearRegions:
            return `${geo.region?.value} e regioni vicine`;
        case GeoLevel.Italia:
            return geo.region.value;
        default:
            return '';
    }
}
/**
 * Returns the inclusion label ("a" or "in") based on the GeoLevel
 *
 * e.g. Annunci _a_ Milano, Annunci _in_ Lombardia
 */
function getGeoInclusionLabel(level) {
    if (level === GeoLevel.Town || level === GeoLevel.City) {
        return 'a';
    }
    return 'in';
}
const TuttaItalia = {
    id: '0',
    friendlyName: 'italia',
    label: 'Regione',
    level: 0,
    uri: '/geo/region',
    value: 'Tutta Italia',
};
/**
 * @deprecated if needed, use the one from sbt-web-geo-search
 */
class GeogosToAutocomplete {
    /**
     * @deprecated if needed, use the one from sbt-web-geo-search
     */
    translate(geogosResponse) {
        const dataToAutocomplete = [];
        const entries = geogosResponse.entries || [];
        const MAX_SUGGESTIONS = Math.min(entries.length, 10);
        for (let i = 0; i < MAX_SUGGESTIONS; i++) {
            const entry = entries[i];
            if (entry.town) {
                dataToAutocomplete.push(this.getTownData(entry));
            }
            else if (entry.city) {
                dataToAutocomplete.push(this.getCityData(entry));
            }
            else {
                dataToAutocomplete.push(this.getRegionData(entries[i]), this.getRegionsData(entries[i]));
            }
        }
        return dataToAutocomplete;
    }
    getTownData(entry) {
        return {
            value: { entry },
            input_label: `${entry.town?.value} (${entry.city?.shortName})`,
            options_labels: [
                `${entry.town?.value} `,
                `(${entry.city?.shortName}) comune`,
            ],
        };
    }
    getCityData(entry) {
        return {
            value: { entry },
            input_label: `${entry.city?.value} e provincia`,
            options_labels: [`${entry.city?.value} `, 'e provincia'],
        };
    }
    getRegionsData(entry) {
        return {
            value: { entry, searchNearRegions: true },
            input_label: `${entry.region?.value} e regioni vicine`,
            options_labels: [`${entry.region?.value} `, 'e regioni vicine'],
        };
    }
    getRegionData(entry) {
        return {
            value: { entry },
            input_label: entry.region?.value,
            options_labels: [`${entry.region?.value} `, 'regione'],
        };
    }
}

const CampaignResponseSchema = {
    ...genericResponseSchema,
    campaigns: 'data.campaigns',
};
({
    ...genericResponseSchema,
    testDriveItem: 'data.payload',
});
({
    ...genericResponseSchema,
    carConfiguratorItem: 'data.payload',
});
const UsefulLinksResponseSchema = {
    ...genericResponseSchema,
    usefulLinks: 'data.payload',
};

class HebeClient {
    httpClient;
    constructor(baseUrl, apiPath, channel) {
        this.httpClient = clientFactory(baseUrl, apiPath, channel);
    }
    /**
     * Get Campaigns from Hebe with params
     * @param params
     */
    async getCampaigns(params, data) {
        const resp = await this.httpClient.post(`/campaigns/lookup`, data, {
            params,
        });
        return morphism(CampaignResponseSchema, resp);
    }
    async searchUsefulLinks(categoryId, category, advertiser_id, advertiserType, ad_type, property, regionId, city, cityId, townId, price, brand, model, vehicle_status, ship_length, ad_id) {
        const request = this.buildRequest(categoryId, category, advertiser_id, advertiserType, ad_type, property, regionId, city, cityId, townId, price, brand, model, vehicle_status, ship_length, ad_id);
        const resp = await this.httpClient.post('/campaigns/lookup', request);
        return morphism(UsefulLinksResponseSchema, resp);
    }
    buildRequest(categoryId, category, advertiser_id, advertiserType, ad_type, property, regionId, city, cityId, townId, price, brand, model, vehicle_status, ship_length, ad_id) {
        return {
            category: categoryId,
            price: parseInt(price || '0', 10),
            advertiser_id,
            advertiser_type: this.getAdvertiserType(advertiserType),
            ad_type,
            property,
            position: 'AD_DETAIL_BELOW_PRICE',
            geo: {
                region: regionId,
                city: cityId,
                town: townId,
            },
            placeholders: {
                category,
                city,
                price,
                brand,
                model,
                vehicle_status,
                ship_length,
                ad_id,
            },
        };
    }
    getAdvertiserType(advertiserType) {
        switch (advertiserType) {
            case 0:
                return 'privato';
            case 1:
                return 'impresa-piu';
            case 2:
                return 'azienda-non-pro';
            default:
                return '';
        }
    }
}

class HebeClientV2 {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel, timeout);
    }
    /**
     * Get Campaigns from Hebe with params
     * @param params
     * @param data
     */
    async getLookupCampaigns(params, data) {
        const path = `/campaigns/query/lookup`;
        const [err, resp] = await to(this.httpClient.post(path, data, {
            params,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

const shopQueryPayloadSchema = {
    name: 'name',
    logo: 'logo',
    openingTime: 'opening_time',
    adsCount: 'ads_count',
    address: 'address',
    urls: 'urls',
    website: 'website',
    shopId: 'shop_id',
};
const shopQueryResponseSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: morphism(shopQueryPayloadSchema),
    },
};

const DEFAULT_TIMEOUT_MS$3 = 80;
class HephaestusClient {
    httpClient;
    timeout;
    constructor(baseUrl, channel, timeout = DEFAULT_TIMEOUT_MS$3) {
        this.timeout = timeout;
        this.httpClient = clientFactory(baseUrl, '/v1/shops-q', channel, timeout);
    }
    async getShop(shopID) {
        const [err, resp] = await to(this.httpClient.get(`/${shopID}`));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (err?.response?.status == HTTPStatusCode.NotFound) {
            return {
                ...notFoundResponseError,
                info: 'Shop not found',
                payload: err.response.data,
            };
        }
        return handleResponse(err, resp, shopQueryResponseSchema);
    }
}

const deleteReasonsResponseSchema = {
    ...genericResponseSchema,
    deleteReasons: 'data',
};

class HeraclesClient {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel, timeout);
    }
    async getDeleteReasons(userId, headers) {
        const path = `/users/${userId}/ads/delete-reasons`;
        const [err, rawResp] = await to(this.httpClient.get(path, {
            headers,
            withCredentials: true,
        }));
        return handleResponse(err, rawResp, deleteReasonsResponseSchema);
    }
    async deleteAd(request, headers) {
        const path = `/listing/user/${request.user_id}/item/${request.ad_id}`;
        const data = JSON.stringify(request);
        const [err, rawResp] = await to(this.httpClient.delete(path, {
            data,
            headers,
            withCredentials: true,
        }));
        return handleResponse(err, rawResp, genericResponseSchema);
    }
    async deleteAdByAdmin(request) {
        const path = `/listing/admin/item/${request.ad_id}`;
        const data = JSON.stringify({ role: 'admin', ...request });
        const headers = { 'Content-Type': 'application/json;charset=UTF-8' };
        const [err, rawResp] = await to(this.httpClient.delete(path, {
            data,
            headers,
            withCredentials: true,
        }));
        return handleResponse(err, rawResp, genericResponseSchema);
    }
}

const geocodeResponseSchema = {
    ...genericResponseSchema,
    payload: 'data.items',
};

const hereMapsBaseUrl = 'https://geocode.search.hereapi.com';
// The Here API rejects Options calls so this is commented for now.
// const commonHeaders = Object.create(null, {
//   'X-Request-ID': {
//     // Every request reads this property and gets a new value
//     get() {
//       return uuid();
//     },
//     enumerable: true,
//   },
// });
const commonParams = Object.create(null, {
    lang: { value: 'it', enumerable: true },
});
class HereMapsClient {
    httpClient;
    /**
     * Constructs a pre-configured client.
     * @param {Object} config The configuration for the client. The API key must
     * be passed.
     * @param {string} config.apiKey The API Key to send to Here's API.
     * @param {string} config.baseUrl An override of the base URL to make the
     * requests against. Leaving this undefined results in the Here production
     * API being used.
     */
    constructor({ apiKey, baseUrl = hereMapsBaseUrl }) {
        this.httpClient = clientFactory(baseUrl, '/v1', undefined);
        this.httpClient.defaults.params = { ...commonParams, apiKey };
    }
    /**
     * Searches against the Geocode endpoint for results appropriate for the
     * query. The results are limited to Italy.
     * @param {string} query The string to search for.
     */
    async geocode(query) {
        const [err, resp] = await to(this.httpClient.get('geocode', {
            params: { q: query, in: 'countryCode:ITA', limit: '5' },
        }));
        const result = handleResponse(err, resp, geocodeResponseSchema);
        if (err) {
            throw result;
        }
        else {
            return result;
        }
    }
}

const buyerInfoSchema = {
    email: 'email',
    forwardPhone: 'forward_phone',
    phone: 'phone',
    name: 'name',
    userId: 'user_id',
};
const replySchema = {
    email: 'email',
    name: 'name',
    body: 'body',
    cc: 'cc',
    acceptTermsConds: 'accept_terms_conds',
    forwardPhone: 'forward_phone',
    phone: 'phone',
};
const conversationsSchema = {
    items: 'items',
    size: 'size',
    userId: 'userId',
    nextParams: 'nextParams',
    previousParams: 'previousParams',
    embedded: '_embedded',
    links: '_links',
};
const getProMessagingSchema = {
    enabled: 'enabled',
};
const getBuyerInfoResponseSchema = {
    ...genericResponseSchema,
    buyerInfo: {
        path: 'data',
        fn: morphism(buyerInfoSchema),
    },
};
const adReplyResponseSchema = {
    ...genericResponseSchema,
    reply: {
        path: 'data',
        fn: morphism(replySchema),
    },
};
const conversationsResponseSchema = {
    ...genericResponseSchema,
    conversations: {
        path: 'data',
        fn: morphism(conversationsSchema),
    },
};
const getProMessagingResponseSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: morphism(getProMessagingSchema),
    },
};

class HermesClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async getBuyerInfo(userID) {
        const [err, resp] = await to(this.httpClient.get(`/reply/users/${userID}/info`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getBuyerInfoResponseSchema);
    }
    async getConversations(loggedInUserID, partnerID, itemID) {
        const [err, resp] = await to(this.httpClient.get(`/mcproxy/api/hal/${loggedInUserID}/conversations?partnerId=${partnerID}&itemId=${itemID}&itemType=ad`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, conversationsResponseSchema);
    }
    async sendAdReply(adID, body, buyerInfo, attachment) {
        let data = new FormData();
        const req = {
            accept_terms_conds: true,
            cc: false,
            name: buyerInfo.name,
            phone: buyerInfo.phone,
            forward_phone: buyerInfo.forwardPhone,
            body,
        };
        if (attachment) {
            data.append('request', new Blob([JSON.stringify(req)], {
                type: 'application/json',
            }), 'request');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            data.append('attach', attachment);
        }
        else {
            data = JSON.stringify(req);
        }
        const [err, resp] = await to(this.httpClient.post(`/reply/${adID}`, data, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, adReplyResponseSchema);
    }
    async getProMessaging(userID) {
        const [err, resp] = await to(this.httpClient.get(`/reply/users/${userID}/messaging-pro`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getProMessagingResponseSchema);
    }
    async updateProMessaging(userID, enabled) {
        const body = {
            enabled,
        };
        const [err, resp] = await to(this.httpClient.post(`/reply/users/${userID}/messaging-pro/toggle`, body, {
            withCredentials: true,
        }));
        return handleGenericResponse(err, resp);
    }
}

const getProductsListResponseSchema = {
    ...genericResponseSchema,
    products: 'data',
};
const getProductMultiplierResponseSchema = {
    ...genericResponseSchema,
    productMultiplier: 'data',
};
const putProductMultiplierResponseSchema = {
    ...genericResponseSchema,
    productMultiplier: 'data',
};
const putBulkProductMultiplierResponseSchema = {
    ...genericResponseSchema,
    productsMultiplier: 'data',
};
const getCountersByUserResponseSchema = {
    ...genericResponseSchema,
    counters: 'data',
};
const getCountersByUserExportResponseSchema = {
    ...genericResponseSchema,
    csv: 'data',
};
const resetAdInsertionCountersByUserResponseSchema = genericResponseSchema;

const commonPath$1 = `/api/products`;
class HerseClient {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/', channel, timeout);
    }
    async getProductsList(params) {
        const [err, rawResp] = await to(this.httpClient.get(commonPath$1, { params }));
        return handleResponse(err, rawResp, getProductsListResponseSchema);
    }
    async getDefaultMultiplier() {
        const [err, rawResp] = await to(this.httpClient.get('/api/default-multiplier'));
        return handleResponse(err, rawResp, getProductMultiplierResponseSchema);
    }
    async getProductMultiplier(id) {
        const [err, rawResp] = await to(this.httpClient.get(`${commonPath$1}/${id}/multiplier`));
        return handleResponse(err, rawResp, getProductMultiplierResponseSchema);
    }
    async putProductMultiplier(id, value) {
        const [err, rawResp] = await to(this.httpClient.put(`${commonPath$1}/${id}/multiplier`, { value }));
        return handleResponse(err, rawResp, putProductMultiplierResponseSchema);
    }
    async putProductBulkMultiplier(categories, adsLimit, multiplier) {
        const [err, rawResp] = await to(this.httpClient.put(`${commonPath$1}/${categories}/${adsLimit}/multiplier`, { value: multiplier }));
        return handleResponse(err, rawResp, putBulkProductMultiplierResponseSchema);
    }
    async getCountersByUser(params) {
        const [err, rawResp] = await to(this.httpClient.get('/api/counters/by-user', { params }));
        return handleResponse(err, rawResp, getCountersByUserResponseSchema);
    }
    async getCountersByUserExport(params) {
        const [err, rawResp] = await to(this.httpClient.get('/api/counters/by-user-export', {
            params,
            responseType: 'blob',
        }));
        return handleResponse(err, rawResp, getCountersByUserExportResponseSchema);
    }
    async resetAdInsertionCountersByUser(params) {
        const [err, rawResp] = await to(this.httpClient.post('/api/adinsertion/counters/reset', { ...params }));
        return handleResponse(err, rawResp, resetAdInsertionCountersByUserResponseSchema);
    }
}

const socialLoginSchemaNewUser = {
    ...genericResponseSchema,
    token: 'data.token',
    temporaryAccountId: 'data.account_id',
};
const socialLoginSchema = {
    ...genericResponseSchema,
    userId: 'data.user_id',
    trackingId: 'data.tracking_id',
    secret: 'data.secret',
};

class HestiaClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1/users/social', channel);
    }
    isANewUserResponseGuard = (response) => 'temporaryAccountId' in response;
    async socialLogin(idToken, type, authJWT, headers) {
        const data = {
            idtoken: idToken,
            type,
        };
        const socialLoginHeaders = {
            ...headers,
            Authorization: `Bearer ${authJWT}`,
        };
        const [err, resp] = await to(this.httpClient.post(`/login`, data, { headers: socialLoginHeaders }));
        const schema = resp?.status === HTTPStatusCode.OK
            ? socialLoginSchema
            : socialLoginSchemaNewUser;
        return handleResponse(err, resp, schema);
    }
    async socialLoginAddTos(accountId, token, tos, authJWT, xForwardedFor) {
        const data = {
            tos,
            token,
        };
        const headers = {
            Authorization: `Bearer ${authJWT}`,
        };
        if (xForwardedFor) {
            headers['x-forwarded-for'] = xForwardedFor;
        }
        const [err, resp] = await to(this.httpClient.put(`/${accountId}/account`, data, { headers }));
        return handleResponse(err, resp, socialLoginSchema);
    }
}

const getHyperwalletTransferMethodResponseSchema = {
    ...genericResponseSchema,
    transferMethod: 'data',
};
const getHyperwalletTransferMethodsResponseSchema = {
    ...genericResponseSchema,
    transferMethods: 'data.data',
};

class HyperwalletClient {
    httpClient;
    constructor(baseUrl) {
        this.httpClient = clientFactory(baseUrl, '/rest/v3', undefined);
    }
    appendHeader(key, value) {
        this.httpClient.defaults.headers = {
            ...this.httpClient.defaults.headers,
            [key]: value,
        };
    }
    setAuthToken(token) {
        this.appendHeader('Authorization', `Bearer ${token}`);
    }
    async getTransferMethod(userID, transferMethodID) {
        const [err, resp] = await to(this.httpClient.get(`/users/${userID}/transfer-methods/${transferMethodID}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getHyperwalletTransferMethodResponseSchema);
    }
    async getTransferMethods(userID) {
        const [err, resp] = await to(this.httpClient.get(`/users/${userID}/transfer-methods`, {
            withCredentials: true,
            params: {
                status: 'ACTIVATED',
            },
        }));
        return handleResponse(err, resp, getHyperwalletTransferMethodsResponseSchema);
    }
    async createPayPal(userID, email) {
        const [err, resp] = await to(this.httpClient.post(`/users/${userID}/transfer-methods`, {
            transferMethodCountry: 'IT',
            transferMethodCurrency: 'EUR',
            type: 'PAYPAL_ACCOUNT',
            email,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getHyperwalletTransferMethodResponseSchema);
    }
    async updatePayPal(userID, transferMethodID, email) {
        const [err, resp] = await to(this.httpClient.put(`/users/${userID}/transfer-methods/${transferMethodID}`, {
            transferMethodCountry: 'IT',
            transferMethodCurrency: 'EUR',
            type: 'PAYPAL_ACCOUNT',
            email,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getHyperwalletTransferMethodResponseSchema);
    }
    async createBankAccount(userID, profileType, bicSwift, iban) {
        const [err, resp] = await to(this.httpClient.post(`/users/${userID}/transfer-methods`, {
            transferMethodCountry: iban.slice(0, 2).toUpperCase(),
            transferMethodCurrency: 'EUR',
            profileType,
            type: 'BANK_ACCOUNT',
            bankId: bicSwift,
            bankAccountId: iban,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getHyperwalletTransferMethodResponseSchema);
    }
    async updateBankAccount(userID, transferMethodID, profileType, bicSwift, iban) {
        const [err, resp] = await to(this.httpClient.put(`/users/${userID}/transfer-methods/${transferMethodID}`, {
            transferMethodCountry: iban.slice(0, 2).toUpperCase(),
            transferMethodCurrency: 'EUR',
            profileType,
            type: 'BANK_ACCOUNT',
            bankId: bicSwift,
            bankAccountId: iban,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getHyperwalletTransferMethodResponseSchema);
    }
}

const notificationSettingDataSchema = {
    resourceId: 'resource_id',
    push: 'push',
    email: 'email',
};
const getNotificationsChannelsSettingsResponseSchema = {
    ...genericResponseSchema,
    notificationsChannelsSettings: {
        path: 'data.notitications_channels_settings',
        fn: morphism(notificationSettingDataSchema),
    },
};

class IrisClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1/notifications', channel);
    }
    async queryByUserId(userId) {
        if (!userId) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: User cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.get(`/users/${userId}/channels/settings/saved_searches`));
        return handleResponse(err, resp, getNotificationsChannelsSettingsResponseSchema);
    }
    async updateEmailNotificationsSetting(userId, searchId, active) {
        if (!searchId || !userId) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: User and search id cannot be empty`,
            };
        }
        const payload = { email: active };
        const [err, resp] = await to(this.httpClient.patch(`/users/${userId}/channels/settings/saved_searches/${searchId}`, payload));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

const postItSchema = {
    CreatedAt: 'created_at',
    ID: 'id',
    AdminID: 'admin_id',
    Body: 'body',
    UID: 'uid',
    UserID: 'user_id',
    AdminName: 'admin_name',
    Abuse: 'abuse',
};
const getPostItsResponseSchema = {
    ...genericResponseSchema,
    post_its: {
        path: 'data',
        fn: morphism(postItSchema),
    },
};

class LavernaClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/', channel);
    }
    async getPostIts(userId) {
        const [err, resp] = await to(this.httpClient.get(`/post-it/user-id/${userId}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPostItsResponseSchema);
    }
    async deletePostIt(postID) {
        const headers = { 'Content-Type': 'application/json;charset=UTF-8' };
        const [err, rawResp] = await to(this.httpClient.delete(`/post-it/${postID}`, {
            headers,
            withCredentials: true,
        }));
        return handleResponse(err, rawResp, genericResponseSchema);
    }
}

const getGDPRRequestsListSchema = {
    ...genericResponseSchema,
    requestsObj: 'data',
};
const getArchiveUrlSchema = {
    ...genericResponseSchema,
    archiveObj: 'data',
};

class LetheClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async accountDeletionByAdmin(userId) {
        const [err, resp] = await to(this.httpClient.post(`/private/privacy/users/${userId}/deletion`, { user_id: userId }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async accountDeletionRequest(userId, bottega) {
        const config = {
            withCredentials: true,
            headers: {},
        };
        if (bottega && config.headers) {
            config.headers.Cookie = `bottega=${bottega}`;
        }
        const [err, resp] = await to(this.httpClient.post(`/privacy/users/${userId}/deletion/email`, null, config));
        return handleGenericResponse(err, resp);
    }
    async accountDeletionConfirm(userId, token, bottega) {
        const config = {
            withCredentials: true,
            headers: {},
            params: { token },
        };
        if (bottega && config.headers) {
            config.headers.Cookie = `bottega=${bottega}`;
        }
        const [err, resp] = await to(this.httpClient.post(`/privacy/users/${userId}/deletion`, null, config));
        return handleGenericResponse(err, resp);
    }
    async accountTakeoutByAdmin(userEmail, userId) {
        const [err, resp] = await to(this.httpClient.post(`/private/privacy/users/${userId}/takeout`, { email: userEmail }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async getGDPRRequestsList(userId, type, bottega, adminRequest) {
        const config = {
            withCredentials: true,
            headers: {},
            params: { type },
        };
        let route = `/privacy/users/${userId}/requests`;
        if (adminRequest) {
            route = `/private${route}`;
        }
        if (bottega && config.headers) {
            config.headers.Cookie = `bottega=${bottega}`;
        }
        const [err, resp] = await to(this.httpClient.get(route, config));
        return handleResponse(err, resp, getGDPRRequestsListSchema);
    }
    async getArchive(userId, requestId, archiveName) {
        const [err, resp] = await to(this.httpClient.get(`/private/privacy/users/${userId}/requests/${requestId}/archive/${archiveName}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getArchiveUrlSchema);
    }
}

const seoMetaPayloadSchema = {
    canonicalUrl: 'canonical_url',
    title: 'title',
    h1: 'h1',
    description: 'description',
    metaRobots: 'meta_robots',
};
const getSeoMetaResponseSchema = {
    ...genericResponseSchema,
    type: 'data.type',
    payload: {
        path: 'data.payload',
        fn: morphism(seoMetaPayloadSchema),
    },
};

const DEFAULT_TIMEOUT_MS$2 = 80;
class MetiClient {
    httpClient;
    timeout;
    constructor(baseUrl, channel, timeout = DEFAULT_TIMEOUT_MS$2) {
        this.timeout = timeout;
        this.httpClient = clientFactory(baseUrl, '/v1/private/seo', channel, timeout);
    }
    async getSeoConfiguration(querystring) {
        if (!querystring) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: querystring cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.get(`configuration?${querystring}`));
        if (err && err.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (err && err.response && err.response.status == HTTPStatusCode.NotFound) {
            return {
                ...notFoundResponseError,
                info: `Configuration not found`,
                payload: err.response.data,
            };
        }
        return handleResponse(err, resp, getSeoMetaResponseSchema);
    }
}

const savedSearchDataSchema = {
    filters: 'filters',
    searchId: 'search_id',
    insertionDate: 'insertion_date',
    updateDate: 'update_date',
    sort: 'sort',
};
const getSavedSearchResponseSchema = {
    ...genericResponseSchema,
    savedSearch: {
        path: 'data',
        fn: morphism(savedSearchDataSchema),
    },
};
const getSavedSearchesResponseSchema = {
    ...genericResponseSchema,
    savedSearches: {
        path: 'data.saved_searches',
        fn: morphism(savedSearchDataSchema),
    },
};

class MnemeClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1/searches-q', channel);
    }
    async save(payload, userId) {
        const [err, resp] = await to(this.httpClient.post(`/users/${userId}/searches`, payload));
        return handleResponse(err, resp, getSavedSearchResponseSchema);
    }
    async queryByUserId(userId, bottega) {
        if (!userId) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: User cannot be empty`,
            };
        }
        const headers = {};
        if (bottega) {
            headers['Cookie'] = `bottega=${bottega}`;
        }
        const [err, resp] = await to(this.httpClient.get(`/users/${userId}/searches`, { headers }));
        return handleResponse(err, resp, getSavedSearchesResponseSchema);
    }
    async delete(userId, searchId) {
        if (!userId) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: UserId cannot be empty`,
            };
        }
        if (!searchId) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: SearchId cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.delete(`/users/${userId}/searches/${searchId}`));
        return handleResponse(err, resp, getSavedSearchesResponseSchema);
    }
}

const favoriteAdSchema = {
    urn: 'urn',
    insertDate: 'insert_date',
};
const getFavoritesResponseSchema$1 = {
    ...genericResponseSchema,
    favorites: {
        path: 'data.favorite_ads',
        fn: morphism(favoriteAdSchema),
    },
};
const getFavoritesStatsResponseSchema = {
    ...genericResponseSchema,
    counter: {
        path: 'data.counter',
        fn: (propertyValue) => propertyValue
            ? {
                value: Number(propertyValue.key),
            }
            : undefined,
    },
};

class MnemosyneClient {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/v1/favorites', channel, timeout);
    }
    async getUserStatistics(adId) {
        const [err, resp] = await to(this.httpClient.get(`/ads/${adId}/stats`));
        return handleResponse(err, resp, getFavoritesStatsResponseSchema);
    }
    async getFavorites(userId) {
        const [err, resp] = await to(this.httpClient.get(`/search/users/${userId}/items`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getFavoritesResponseSchema$1);
    }
    async saveFavorite(urn, userId) {
        const [err, resp] = await to(this.httpClient.post(`/search/users/${userId}/items`, { urn }, {
            withCredentials: true,
        }));
        return handleGenericResponse(err, resp);
    }
    async unsaveFavorite(urn, userId) {
        const [err, resp] = await to(this.httpClient.delete(`/search/users/${userId}/items`, {
            params: { urns: urn },
            withCredentials: true,
        }));
        return handleGenericResponse(err, resp);
    }
    async unsaveFavorites(urns, userId) {
        const [err, resp] = await to(this.httpClient.delete(`/search/users/${userId}/items`, {
            params: { urns: urns.join(',') },
            withCredentials: true,
        }));
        return handleGenericResponse(err, resp);
    }
}

var ImageSize;
(function (ImageSize) {
    ImageSize["Small"] = "small";
    ImageSize["Slider"] = "slider";
    ImageSize["Medium"] = "medium";
    ImageSize["Gallery"] = "gallery";
    ImageSize["Big"] = "big";
    ImageSize["BigCard"] = "bigcard";
})(ImageSize || (ImageSize = {}));

var AdTypes;
(function (AdTypes) {
    AdTypes["Sell"] = "s";
    AdTypes["Seek"] = "k";
    AdTypes["Rent"] = "u";
    AdTypes["VacationRent"] = "h";
    AdTypes["GiveAway"] = "g";
})(AdTypes || (AdTypes = {}));

var AdvertiserType;
(function (AdvertiserType) {
    AdvertiserType[AdvertiserType["Private"] = 0] = "Private";
    AdvertiserType[AdvertiserType["ImpresaPiu"] = 1] = "ImpresaPiu";
    AdvertiserType[AdvertiserType["Company"] = 2] = "Company";
})(AdvertiserType || (AdvertiserType = {}));

var HebeCampaignType;
(function (HebeCampaignType) {
    HebeCampaignType["LINK"] = "LINK";
    HebeCampaignType["TEXTLINK"] = "TEXTLINK";
    HebeCampaignType["CARD_BLOCK"] = "CARD_BLOCK";
})(HebeCampaignType || (HebeCampaignType = {}));
var HebeCampaignPage;
(function (HebeCampaignPage) {
    HebeCampaignPage["LISTING"] = "LISTING";
    HebeCampaignPage["ADDETAIL"] = "AD_DETAIL";
    HebeCampaignPage["MANAGE_ADS"] = "MANAGE_ADS";
})(HebeCampaignPage || (HebeCampaignPage = {}));
var HebeCampaignPosition;
(function (HebeCampaignPosition) {
    // AD DETAIL
    HebeCampaignPosition["BELOW_PRICE"] = "BELOW_PRICE";
    HebeCampaignPosition["BELOW_DESCRIPTION"] = "BELOW_DESCRIPTION";
    HebeCampaignPosition["BELOW_HISTORY"] = "BELOW_HISTORY";
    HebeCampaignPosition["BELOW_FEATURES"] = "BELOW_FEATURES";
    HebeCampaignPosition["BELOW_ADVERTISER"] = "BELOW_ADVERTISER";
    HebeCampaignPosition["ASIDE"] = "ASIDE";
    // LISTING
    HebeCampaignPosition["NINTH_POSITION"] = "NINTH_POSITION";
    HebeCampaignPosition["ADS_FILTERS"] = "ADS_FILTERS";
    // MANAGE ADS
    HebeCampaignPosition["TOP_ADS"] = "TOP_ADS";
})(HebeCampaignPosition || (HebeCampaignPosition = {}));
var HebeCampaignProperty;
(function (HebeCampaignProperty) {
    HebeCampaignProperty["WEB"] = "web";
    HebeCampaignProperty["MSITE"] = "m-site";
    HebeCampaignProperty["ANDROID"] = "android";
    HebeCampaignProperty["IOS"] = "ios";
})(HebeCampaignProperty || (HebeCampaignProperty = {}));

var PrivateBannerPages;
(function (PrivateBannerPages) {
    PrivateBannerPages["PROMOTE"] = "PROMOTE";
    PrivateBannerPages["MANAGEADS"] = "MANAGEADS";
    PrivateBannerPages["LEGACY_ADIN_STEP_1"] = "LEGACY_ADIN_STEP_1";
})(PrivateBannerPages || (PrivateBannerPages = {}));
var PrivateBannerEntryPoints;
(function (PrivateBannerEntryPoints) {
    PrivateBannerEntryPoints["NEWAD"] = "NEWAD";
    PrivateBannerEntryPoints["EDITAD"] = "EDITAD";
    PrivateBannerEntryPoints["PROMOTE"] = "PROMOTE";
    PrivateBannerEntryPoints["RESERVEDAREA"] = "RESERVEDAREA";
})(PrivateBannerEntryPoints || (PrivateBannerEntryPoints = {}));

/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
/**
 * @deprecated Use `FeatureURI` from sbt-web/network instead.
 */
var FeatureURI;
(function (FeatureURI) {
    FeatureURI["AdType"] = "/type";
    FeatureURI["AdvertiserType"] = "/advertiser_type";
    FeatureURI["AirConditioning"] = "/air_conditioning";
    FeatureURI["AllServicings"] = "/all_servicings";
    FeatureURI["AnimalType"] = "/animal_type";
    FeatureURI["AudiovideoType"] = "/audiovideo_type";
    FeatureURI["AvailableNow"] = "/available_now";
    FeatureURI["Balcony"] = "/balcony";
    FeatureURI["Bathrooms"] = "/bathrooms";
    FeatureURI["BicycleType"] = "/bicycle_type";
    FeatureURI["Bike"] = "/bike";
    FeatureURI["BikeModel"] = "/bike/model";
    FeatureURI["BookType"] = "/book_type";
    FeatureURI["Brand"] = "/brand";
    FeatureURI["BuildingCondition"] = "/building_condition";
    FeatureURI["Car"] = "/car";
    FeatureURI["CaravanType"] = "/caravan_type";
    FeatureURI["CarType"] = "/car_type";
    FeatureURI["Category"] = "/category";
    FeatureURI["CategoryFacet"] = "/category_facet";
    FeatureURI["ChildrenAge"] = "/children_age";
    FeatureURI["ChildrenType"] = "/children_type";
    FeatureURI["ClothingGender"] = "/clothing_gender";
    FeatureURI["ClothingNumber"] = "/clothing_number";
    FeatureURI["ClothingType"] = "/clothing_type";
    FeatureURI["Color"] = "/color";
    FeatureURI["ComputerType"] = "/computer_type";
    FeatureURI["Consumption"] = "/consumption";
    FeatureURI["ConsumptionSearchable"] = "/consumption_searchable";
    FeatureURI["ContractType"] = "/contract_type";
    FeatureURI["CubicCapacity"] = "/cubic_capacity";
    FeatureURI["Damaged"] = "/damaged";
    FeatureURI["Degree"] = "/degree";
    FeatureURI["Doors"] = "/doors";
    FeatureURI["DriveWheel"] = "/drive_wheel";
    FeatureURI["Elevator"] = "/elevator";
    FeatureURI["EnergyClass"] = "/energy_class";
    FeatureURI["EnginePower"] = "/engine_power";
    FeatureURI["Floor"] = "/floor";
    FeatureURI["Fuel"] = "/fuel";
    FeatureURI["Furnished"] = "/furnished";
    FeatureURI["Garden"] = "/garden";
    FeatureURI["Gearbox"] = "/gearbox";
    FeatureURI["Gender"] = "/gender";
    FeatureURI["Heating"] = "/heating";
    FeatureURI["HobbyType"] = "/hobby_type";
    FeatureURI["HorsePower"] = "/horsepower";
    FeatureURI["ItemShippable"] = "/item_shippable";
    FeatureURI["ItemShippingCost"] = "/item_shipping_cost";
    FeatureURI["ItemShippingCostTuttoSubito"] = "/item_shipping_cost_tuttosubito";
    FeatureURI["ItemShippingPackageSize"] = "/item_shipping_package_size";
    FeatureURI["ItemShippingType"] = "/item_shipping_type";
    FeatureURI["ItemCondition"] = "/item_condition";
    FeatureURI["JobCategory"] = "/job_category";
    FeatureURI["LastFloor"] = "/last_floor";
    FeatureURI["Mileage"] = "/mileage";
    FeatureURI["MileageScalar"] = "/mileage_scalar";
    FeatureURI["Model"] = "/model";
    FeatureURI["MonthlyRent"] = "/monthly_rent";
    FeatureURI["MotorbicycleType"] = "/motorbicycle_type";
    FeatureURI["MultiLevel"] = "/multi_level";
    FeatureURI["Name"] = "/name";
    FeatureURI["NoSalesman"] = "/nosalesman";
    FeatureURI["OfficeType"] = "/office_type";
    FeatureURI["PaintType"] = "/paint_type";
    FeatureURI["Parking"] = "/parking";
    FeatureURI["PhoneType"] = "/phone_type";
    FeatureURI["Pollution"] = "/pollution";
    FeatureURI["Price"] = "/price";
    FeatureURI["Promotions"] = "/promotions";
    FeatureURI["QualitySeal"] = "/quality_seal";
    FeatureURI["QualitySealFlag"] = "/quality_seal_flag";
    FeatureURI["QuerySearchText"] = "/query/search_text";
    FeatureURI["QuerySubjectOnly"] = "/query/subject_only";
    FeatureURI["Reception"] = "/reception";
    FeatureURI["RegisterDate"] = "/register_date";
    FeatureURI["RegMonth"] = "/regmonth";
    FeatureURI["Room"] = "/room";
    FeatureURI["Rooms"] = "/rooms";
    FeatureURI["RoomType"] = "/room_type";
    FeatureURI["Seats"] = "/seats";
    FeatureURI["Service"] = "/service";
    FeatureURI["ShipLength"] = "/ship_length";
    FeatureURI["ShipType"] = "/ship_type";
    FeatureURI["Size"] = "/size";
    FeatureURI["SmokeFree"] = "/smoke_free";
    FeatureURI["Smoker"] = "/smoker";
    FeatureURI["ShippingCarriers"] = "/shipping_carriers";
    FeatureURI["SportType"] = "/sport_type";
    FeatureURI["Surface"] = "/size";
    FeatureURI["TransactionStatus"] = "/transaction_status";
    FeatureURI["TotalOwners"] = "/total_owners";
    FeatureURI["Type"] = "/type";
    FeatureURI["UserId"] = "/user_id";
    FeatureURI["VatDeductible"] = "/vat_deductible";
    FeatureURI["VehicleLength"] = "/vehicle_length";
    FeatureURI["VehicleStatus"] = "/vehicle_status";
    FeatureURI["VehicleType"] = "/vehicle_type";
    FeatureURI["Version"] = "/version";
    FeatureURI["WarrantyMonths"] = "/warranty_months";
    FeatureURI["WorkHour"] = "/work_hour";
    FeatureURI["WorkHours"] = "/work_hour";
    FeatureURI["WorkLevel"] = "/work_level";
    FeatureURI["Year"] = "/year";
    FeatureURI["Cin"] = "/cin";
})(FeatureURI || (FeatureURI = {}));

var GDPRRequestType;
(function (GDPRRequestType) {
    GDPRRequestType["Deletion"] = "DELETION";
    GDPRRequestType["Takeout"] = "TAKEOUT";
})(GDPRRequestType || (GDPRRequestType = {}));
var GDPRRequestStatus;
(function (GDPRRequestStatus) {
    GDPRRequestStatus["PENDING"] = "PENDING";
    GDPRRequestStatus["NOT_SENT"] = "NOT_SENT";
    GDPRRequestStatus["IN_PROGRESS"] = "IN_PROGRESS";
    GDPRRequestStatus["BLOCKED"] = "BLOCKED";
    GDPRRequestStatus["COMPLETED"] = "COMPLETED";
    GDPRRequestStatus["EXPIRED"] = "EXPIRED";
})(GDPRRequestStatus || (GDPRRequestStatus = {}));

var Orders;
(function (Orders) {
    Orders["Relevance"] = "relevance";
    Orders["DateDesc"] = "datedesc";
    Orders["PriceAsc"] = "priceasc";
    Orders["PriceDesc"] = "pricedesc";
    Orders["YearAsc"] = "yearasc";
    Orders["YearDesc"] = "yeardesc";
    Orders["MileageAsc"] = "mileageasc";
    Orders["MileageDesc"] = "mileagedesc";
})(Orders || (Orders = {}));

var State;
(function (State) {
    State["Available"] = "available";
    State["Unavailable"] = "unavailable";
})(State || (State = {}));
var PaidOptionEntryPoint;
(function (PaidOptionEntryPoint) {
    /** @deprecated Use EDITAD */
    PaidOptionEntryPoint["ADEDIT"] = "ADEDIT";
    PaidOptionEntryPoint["EDITAD"] = "EDITAD";
    PaidOptionEntryPoint["NEWAD"] = "NEWAD";
    PaidOptionEntryPoint["PROMOTE"] = "PROMOTE";
    PaidOptionEntryPoint["PROMOMAIL"] = "PROMOMAIL";
    PaidOptionEntryPoint["ADEDITREFUSE"] = "ADEDITREFUSE";
    PaidOptionEntryPoint["ADPROLONG_V2"] = "ADPROLONG_V2";
    PaidOptionEntryPoint["ADPROLONG_V2_EXPIRING"] = "ADPROLONG_V2_EXPIRING";
    PaidOptionEntryPoint["HOMEPAGE"] = "HOMEPAGE";
})(PaidOptionEntryPoint || (PaidOptionEntryPoint = {}));
var AvailablePublicationFee;
(function (AvailablePublicationFee) {
    AvailablePublicationFee["SERVIZI"] = "ad_pay";
    AvailablePublicationFee["ATTREZZATURE"] = "ad_pay_2";
})(AvailablePublicationFee || (AvailablePublicationFee = {}));
var AvailableFee;
(function (AvailableFee) {
    AvailableFee["SERVIZI"] = "ad_pay";
    AvailableFee["ATTREZZATURE"] = "ad_pay_2";
    AvailableFee["MORE_PHOTOS"] = "more_photos";
})(AvailableFee || (AvailableFee = {}));

var PostField;
(function (PostField) {
    PostField["Id"] = "id";
    PostField["Title"] = "title";
    PostField["Slug"] = "slug";
    PostField["Excerpt"] = "excerpt";
    PostField["Content"] = "content";
    PostField["Featured"] = "featured";
    PostField["PublishedAt"] = "published_at";
})(PostField || (PostField = {}));
var OrderByDirection;
(function (OrderByDirection) {
    OrderByDirection["Ascending"] = "ASC";
    OrderByDirection["Descending"] = "DESC";
})(OrderByDirection || (OrderByDirection = {}));

var TypeProvider;
(function (TypeProvider) {
    TypeProvider["SUBSCRIPTIONS"] = "SUBSCRIPTIONS";
    TypeProvider["CUSTOM"] = "CUSTOM";
})(TypeProvider || (TypeProvider = {}));

var PresenceStatus;
(function (PresenceStatus) {
    PresenceStatus["Offline"] = "OFFLINE";
    PresenceStatus["Online"] = "ONLINE";
})(PresenceStatus || (PresenceStatus = {}));

var UserState;
(function (UserState) {
    UserState["Active"] = "ACTIVE";
    UserState["Unverified"] = "UNVERIFIED";
})(UserState || (UserState = {}));

const resumeLoginSchema = {
    secret: 'secret',
    trackingId: 'tracking_id',
    userId: 'user_id',
};
const userStatusResponseSchema = {
    ...genericResponseSchema,
    userStatus: 'data',
};
const resumeLoginResponseSchema = {
    ...genericResponseSchema,
    resumeLogin: {
        path: 'data',
        fn: morphism(resumeLoginSchema),
    },
};
const userSchema = {
    userID: 'user_id',
    email: 'email',
    status: 'status',
    creationDate: 'creation_date',
};
const userResponseSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: morphism(userSchema),
    },
};

const DEFAULT_TIMEOUT_MS$1 = 100;
class NarcissusClient {
    httpClient;
    timeout;
    constructor(baseUrl, channel, timeout = DEFAULT_TIMEOUT_MS$1) {
        this.timeout = timeout;
        this.httpClient = clientFactory(baseUrl, '/v1', channel, timeout);
    }
    /**
     * Request a password reset
     */
    async resetPassword(email) {
        if (!email) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: email cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.delete(`users/${email}/info/password`));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (resp?.status == HTTPStatusCode.BadRequest) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: email is invalid`,
            };
        }
        if (resp?.status == HTTPStatusCode.InternalServerError) {
            return {
                ...invalidRequestError,
                info: `Internal server error`,
            };
        }
        return handleGenericResponse(err, resp);
    }
    /**
     * Save a new password after the password reset
     */
    async saveNewPassword(userId, otk, password) {
        if (!userId) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: userId cannot be empty`,
            };
        }
        if (!otk) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: otk cannot be empty`,
            };
        }
        if (!password) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: password cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.post(`users/${userId}/info/password`, {
            otk,
            password,
        }));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (resp?.status == HTTPStatusCode.BadRequest) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: Bad Request`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.Forbidden) {
            return {
                ...invalidRequestError,
                info: `Forbidden`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.InternalServerError) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: internal server error`,
            };
        }
        return handleGenericResponse(err, resp);
    }
    /**
     * Update the user password when logged in
     */
    async changePassword(userId, oldPassword, newPassword, bottega) {
        if (!userId) {
            return {
                ...invalidRequestError,
                info: 'Invalid Request: userId cannot be empty',
            };
        }
        if (!newPassword) {
            return {
                ...invalidRequestError,
                info: 'Invalid Request: newPassword cannot be empty',
            };
        }
        if (!oldPassword) {
            return {
                ...invalidRequestError,
                info: 'Invalid Request: oldPassword cannot be empty',
            };
        }
        const body = {
            current: oldPassword,
            new: newPassword,
        };
        const config = {
            withCredentials: true,
            headers: {},
        };
        if (bottega && config.headers) {
            config.headers.Cookie = `bottega=${bottega}`;
        }
        const [err, resp] = await to(this.httpClient.put(`users/${userId}/info/password`, body, config));
        return handleGenericResponse(err, resp);
    }
    async signup(signupData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload = { ...signupData };
        if (signupData.geo && signupData.geo.region.id != TuttaItalia.id) {
            payload.geo.region.key = signupData.geo.region.id;
            payload.geo.city.key = signupData.geo.city
                ? signupData.geo.city.id
                : null;
            payload.geo.town.key = signupData.geo.town
                ? signupData.geo.town.id
                : null;
        }
        else {
            payload.geo = null;
        }
        if (payload.geo &&
            (payload.geo.city.key === null || payload.geo.town.key === null)) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: location is not valid`,
            };
        }
        if (signupData.birthdate && !this.isValidDate(signupData.birthdate)) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: birthdate is not valid`,
            };
        }
        if (!signupData.name) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: name cannot be empty`,
            };
        }
        if (!signupData.username) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: email cannot be empty`,
            };
        }
        if (!signupData.password) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: password cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.post(`users/signup`, payload));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (resp?.status == HTTPStatusCode.BadRequest) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: Bad Request`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.Forbidden) {
            return {
                ...invalidRequestError,
                info: `Forbidden`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.InternalServerError) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: internal server error`,
            };
        }
        return handleGenericResponse(err, resp);
    }
    async login(loginData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const payload = { ...loginData };
        if (!loginData.username) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: email cannot be empty`,
            };
        }
        if (!loginData.password) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: password cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.post(`users/login`, payload));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (resp?.status == HTTPStatusCode.BadRequest) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: Bad Request`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.Forbidden) {
            return {
                ...invalidRequestError,
                info: `Forbidden`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.InternalServerError) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: internal server error`,
            };
        }
        return handleGenericResponse(err, resp);
    }
    async signupVerify(email, code) {
        if (!code) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: code cannot be empty`,
            };
        }
        if (!email) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: email cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.put(`users/${email}/info/account-status`, {
            status: 'ACTIVE',
        }, {
            headers: {
                'X-Account-Activation': code,
            },
        }));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (resp?.status == HTTPStatusCode.BadRequest) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: Bad Request`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.Forbidden) {
            return {
                ...invalidRequestError,
                info: `Forbidden`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.InternalServerError) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: internal server error`,
            };
        }
        return handleGenericResponse(err, resp);
    }
    async checkPassword(password, strategy, additionalStrings) {
        if (!password) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: password cannot be empty`,
            };
        }
        if (!strategy) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: strategy cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.post(`check-password`, {
            password,
            strategy,
            user_words: additionalStrings,
        }));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (resp?.status == HTTPStatusCode.BadRequest) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: Bad Request`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.Forbidden) {
            return {
                ...invalidRequestError,
                info: `Forbidden`,
                payload: err?.response?.data,
            };
        }
        if (resp?.status == HTTPStatusCode.InternalServerError) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: internal server error`,
            };
        }
        return handleGenericResponse(err, resp);
    }
    async userStatus(userID, jwt) {
        const headers = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
        const [err, resp] = await to(this.httpClient.get(`/users/${userID}/status`, {
            headers,
        }));
        return handleResponse(err, resp, userStatusResponseSchema);
    }
    async impersonateUser(userEmail, adminID) {
        const [err, resp] = await to(this.httpClient.post(`/private/users/impersonate`, {
            email: userEmail,
            admin_id: adminID,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async resumeLogin(jwt) {
        const headers = { Authorization: `Bearer ${jwt}` };
        const [err, resp] = await to(this.httpClient.post(`/users/resume-login`, undefined, {
            headers,
        }));
        return handleResponse(err, resp, resumeLoginResponseSchema);
    }
    /**
     * A simple validation for yyyy-mm-dd dates
     * From: https://stackoverflow.com/a/51759570/208623
     * @param date string
     */
    isValidDate(date) {
        try {
            let dt;
            return ('string' === typeof date &&
                (dt = new Date(date)) &&
                date === dt.toISOString().substring(0, 10));
        }
        catch {
            return false;
        }
    }
    async getUser(jwt, filter) {
        const [err, resp] = await to(this.httpClient.get(`/private/users`, {
            headers: { Authorization: `Bearer ${jwt}` },
            params: filter,
        }));
        return handleResponse(err, resp, userResponseSchema);
    }
}

const subscriptionProductSchema = {
    categories: 'product.categories',
};
const subscriptionSchema = {
    subscriptionId: 'subscription_id',
    statusSubscriptions: 'status_subscriptions',
    startDate: 'start_date',
    endDate: 'end_date',
    product: morphism(subscriptionProductSchema),
};
const subscriptionsSchema = {
    subscriptions: {
        path: 'subscriptions',
        fn: morphism(subscriptionSchema),
    },
};
const getSubscriptionsResponseSchema = {
    ...genericResponseSchema,
    response: {
        path: 'data',
        fn: morphism(subscriptionsSchema),
    },
};

class OboloClient {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel, timeout);
    }
    async getUserSubscriptions(userId, params, headers) {
        const [err, resp] = await to(this.httpClient.get(`/subscriptions/users/${userId}`, {
            params,
            headers,
        }));
        return handleResponse(err, resp, getSubscriptionsResponseSchema);
    }
}

const userBlockedSchema = {
    blocked: 'blocked',
    note: 'note',
    insertAt: 'insert_at',
};
const getUserBlockedSchema = {
    ...genericResponseSchema,
    user: {
        path: 'data',
        fn: morphism(userBlockedSchema),
    },
};

class OrcusClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/', channel);
    }
    async getUserBlocked(email) {
        const url = `/private/user/blocked/${email}`;
        const [err, resp] = await to(this.httpClient.get(url, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getUserBlockedSchema);
    }
}

const privateInfoRawSchema = {
    invoice: 'invoice',
    name: 'name',
    surname: 'surname',
    fiscal_code: 'fiscalCode',
};
const companyInfoRawSchema = {
    company_name: 'companyName',
    company_ref: 'companyRef',
    vat: 'vat',
    tax_code: 'taxCode',
    pec: 'pec',
    sdi: 'sdi',
};
const setBillingInfoRawSchema = createSchema({
    is_company: 'isCompany',
    private_info: {
        path: 'privateInfo',
        fn: morphism(privateInfoRawSchema),
    },
    company_info: {
        path: 'companyInfo',
        fn: morphism(companyInfoRawSchema),
    },
    town: 'town',
    postcode: 'postcode',
    city: 'city',
    address: 'address',
    fax: 'fax',
    phone: 'phone',
}, { undefinedValues: { strip: true } });

// Request
const doPurchaseRequestSchema = {
    purchase_id: 'transactionID',
    purchase_type: 'transactionType',
    items: {
        path: 'prices',
        fn: (prices) => [prices.item, ...prices.extra],
    },
    nonce: 'nonce',
    billing_info: {
        path: 'billingInfo',
        fn: (info) => info
            ? morphism(setBillingInfoRawSchema, info)
            : { private_info: { invoice: false } },
    },
    amount: 'amount',
    voucher: {
        path: 'voucher',
        fn: (voucher) => voucher ?? null,
    },
    metadata: {
        path: 'adURN',
        fn: (URN) => (URN ? { ad: { id: URN } } : null),
    },
};
// Response
const payoutUserPrivateSchema = {
    dateOfBirth: 'date_of_birth',
    firstName: 'first_name',
    lastName: 'last_name',
};
const payoutUserCompanySchema = {
    companyName: 'company_name',
};
const payoutUserSchema = {
    userToken: 'hyperwallet_id',
    canReceivePayments: 'can_receive_payments',
    verificationInProgress: 'verification_in_progress',
    selectedTransferMethod: 'selected_transfer_method.hyperwallet_token',
    address: 'address',
    city: 'city',
    province: 'province',
    country: 'country',
    postalCode: 'postal_code',
    isCompany: 'is_company',
    userPrivate: {
        path: 'private_info',
        fn: morphism(payoutUserPrivateSchema),
    },
    userCompany: {
        path: 'company_info',
        fn: morphism(payoutUserCompanySchema),
    },
};
const getPayoutUserResponseSchema = {
    ...genericResponseSchema,
    user: {
        path: 'data',
        fn: morphism(payoutUserSchema),
    },
};
const getPayoutTokenResponseSchema = {
    ...genericResponseSchema,
    token: 'data',
};
const getBanksResponseSchema = {
    ...genericResponseSchema,
    banks: 'data.banks',
};
const getPurchaseTokenResponseSchema = {
    ...genericResponseSchema,
    token: 'data',
};
const paymentSchema = {
    id: 'payment_id',
};
const doPurchaseResponseSchema = {
    ...genericResponseSchema,
    payment: {
        path: 'data',
        fn: morphism(paymentSchema),
    },
};
const transferMethodIDSchema = {
    hyperwalletToken: 'hyperwallet_token',
};
const setTransferMethodResponseSchema = {
    ...genericResponseSchema,
    transferMethod: {
        path: 'data',
        fn: morphism(transferMethodIDSchema),
    },
};

const PUBLIC_BASE = '/p2p/payments';
class PersephoneClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async getPayoutUser(userId) {
        const [err, resp] = await to(this.httpClient.get(`${PUBLIC_BASE}/users/${userId}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPayoutUserResponseSchema);
    }
    async getPayoutToken() {
        const [err, resp] = await to(this.httpClient.get(`${PUBLIC_BASE}/hyperwallet/token`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPayoutTokenResponseSchema);
    }
    async getPurchaseToken() {
        const [err, resp] = await to(this.httpClient.get(`${PUBLIC_BASE}/purchase_token`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPurchaseTokenResponseSchema);
    }
    async saveUser(userId, userInfo) {
        const info = {
            user_id: userId,
            user_token: userInfo.userToken,
            address: userInfo.address,
            city: userInfo.city,
            province: userInfo.province,
            country: userInfo.country,
            postal_code: userInfo.postalCode,
            private_info: {
                first_name: userInfo.userPrivate?.firstName,
                last_name: userInfo.userPrivate?.lastName,
                date_of_birth: userInfo.userPrivate?.dateOfBirth,
            },
            can_receive_payments: userInfo.canReceivePayments,
            transfer_method: userInfo.selectedTransferMethod,
        };
        const [err, resp] = await to(this.httpClient.put(`${PUBLIC_BASE}/users/${userId}`, info, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getPayoutUserResponseSchema);
    }
    async setTransferMethod(userId, transferMethodId) {
        const [err, resp] = await to(this.httpClient.put(`${PUBLIC_BASE}/users/${userId}/transfer-method`, {
            hyperwallet_token: transferMethodId,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, setTransferMethodResponseSchema);
    }
    async removeTransferMethod(userId, transferMethodId, type) {
        const [err, resp] = await to(this.httpClient.put(`${PUBLIC_BASE}/users/${userId}/transfer-method/disconnect`, {
            hyperwallet_token: transferMethodId,
            type,
        }, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async getBanks(bottega) {
        let headers;
        if (bottega) {
            headers = {
                Cookie: `bottega=${bottega}`,
            };
        }
        const [err, resp] = await to(this.httpClient.get(`${PUBLIC_BASE}/banks`, {
            withCredentials: true,
            headers,
        }));
        return handleResponse(err, resp, getBanksResponseSchema);
    }
    async doPurchase(purchaseData) {
        const payload = morphism(doPurchaseRequestSchema, purchaseData);
        const [err, resp] = await to(this.httpClient.post(`${PUBLIC_BASE}/purchase`, payload, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, doPurchaseResponseSchema);
    }
    async unlockWallet(userId) {
        const [err, resp] = await to(this.httpClient.delete(`/private${PUBLIC_BASE}/users/${userId}/wallet`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

const normalizeValues = (values = []) => {
    // If the feature exists but with no values, we show a dash.
    if (values.length === 0) {
        // This is a long dash
        return '\u2014';
    }
    else {
        return values.map((v) => v.value).join(',');
    }
};
function isNotNull(k) {
    return k !== null;
}
const transformHierarchy = (carDetails) => {
    const getFeatureDataFor = (feature) => {
        const featureData = carDetails.features.find((feat) => feat.uri === feature.uri);
        // If there are no features, we show nothing.
        if (!featureData) {
            return null;
        }
        const { label, values: featureValues, uri } = featureData;
        const value = normalizeValues(featureValues);
        return {
            label,
            value,
            uri,
        };
    };
    const results = [];
    carDetails.hierarchy.forEach((element) => {
        const features = element.features.map(getFeatureDataFor).filter(isNotNull);
        if (features.length > 0) {
            results.push({
                title: element.value,
                features,
            });
        }
    });
    return results;
};
const getSingleVersionSchema = {
    ...genericResponseSchema,
    payload: {
        path: 'data',
        fn: transformHierarchy,
    },
};

class PhaethonClient {
    httpClient;
    constructor(baseUrl, channel, timeoutMs = 500) {
        this.httpClient = clientFactory(baseUrl, '/v1/cars', channel, timeoutMs);
    }
    async getLatestVersion(versionCode, year, month) {
        const params = {
            version_code: versionCode,
            reg_year: year,
            reg_month: month,
        };
        const [err, resp] = await to(this.httpClient.get('/latest-version', { params }));
        return handleResponse(err, resp, getSingleVersionSchema);
    }
    async getSingleVersion(feature) {
        const { brand, model, version } = feature;
        if (!brand) {
            throw new Error('Missing brand parameter');
        }
        else if (!model) {
            throw new Error('Missing model parameter');
        }
        else if (!version) {
            throw new Error('Missing version parameter');
        }
        const safeBrand = encodeURIComponent(brand);
        const safeModel = encodeURIComponent(model);
        const safeVersion = encodeURIComponent(version);
        const [err, resp] = await to(this.httpClient.get(`/brands/${safeBrand}/models/${safeModel}/versions/${safeVersion}`));
        return handleResponse(err, resp, getSingleVersionSchema);
    }
}

const faqResponseSchema = {
    ...genericResponseSchema,
    payload: 'data',
};

const DEFAULT_TIMEOUT_MS = 80;
class PitoClient {
    httpClient;
    timeout;
    constructor(baseUrl, channel, timeout = DEFAULT_TIMEOUT_MS) {
        this.timeout = timeout;
        this.httpClient = clientFactory(baseUrl, '/v1/faq', channel, timeout);
    }
    async fetchFaq(queryString) {
        if (!queryString) {
            return {
                ...invalidRequestError,
                info: `Invalid Request: querystring cannot be empty`,
            };
        }
        const [err, resp] = await to(this.httpClient.get(`content?${queryString}`));
        if (err?.code == 'ECONNABORTED') {
            return {
                ...requestTimeoutResponseError,
                info: `Request timeout`,
                payload: undefined,
            };
        }
        if (err?.response?.status == HTTPStatusCode.NotFound) {
            return {
                ...notFoundResponseError,
                info: `Configuration not found`,
                payload: err.response.data,
            };
        }
        return handleResponse(err, resp, faqResponseSchema);
    }
}

const purchasedItemSchema = {
    adId: 'ad_id',
    paidOptions: 'paid_options',
    actionId: 'action_id',
};
const purchasedItemV2Schema = {
    id: 'id',
    amount: 'amount',
    adId: 'ad_id',
    paidOption: 'paid_option',
};
const privateInfoSchema = {
    invoice: 'invoice',
    name: 'name',
    surname: 'surname',
    fiscalCode: 'fiscal_code',
};
const companyInfoSchema = {
    companyName: 'company_name',
    companyRef: 'company_ref',
    vat: 'vat',
    taxCode: 'tax_code',
    pec: 'pec',
    sdi: 'sdi',
};
const billingInfoSchema = createSchema({
    isCompany: 'is_company',
    privateInfo: {
        path: 'private_info',
        fn: morphism(privateInfoSchema),
    },
    companyInfo: {
        path: 'company_info',
        fn: morphism(companyInfoSchema),
    },
    town: 'town',
    postcode: 'postcode',
    city: 'city',
    address: 'address',
    fax: 'fax',
    phone: 'phone',
    valid: 'valid',
}, { undefinedValues: { strip: true } });
const purchaseSchema = {
    transactionId: 'transaction_id',
    amount: 'amount',
    paycode: 'paycode',
    entryPoint: 'entry_point',
    status: 'status',
    items: {
        path: 'items',
        fn: morphism(purchasedItemSchema),
    },
    itemsV2: {
        path: 'items_v2',
        fn: morphism(purchasedItemV2Schema),
    },
    billingInfo: {
        path: 'billing_info',
        fn: morphism(billingInfoSchema),
    },
};
const getPurchaseResponseSchema = {
    ...genericResponseSchema,
    purchase: {
        path: 'data',
        fn: morphism(purchaseSchema),
    },
};
const getBillingInfoResponseSchema = {
    ...genericResponseSchema,
    billingInfo: {
        path: 'data',
        fn: morphism(billingInfoSchema),
    },
};

var Entrypoint;
(function (Entrypoint) {
    Entrypoint["NewAd"] = "newad";
    Entrypoint["EditAd"] = "editad";
    Entrypoint["Promote"] = "promote";
    Entrypoint["Promomail"] = "promomail";
    Entrypoint["Editrefuse"] = "adeditrefuse";
    Entrypoint["Prolong"] = "adprolong_v2";
    Entrypoint["ProlongExpiring"] = "adprolong_v2_expiring";
    Entrypoint["Homepage"] = "homepage";
})(Entrypoint || (Entrypoint = {}));
class PlutusClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/v1', channel);
    }
    async doPurchase(entrypoint, payload, headers) {
        const config = {
            withCredentials: true,
            headers,
        };
        const [err, resp] = await to(this.httpClient.post(`/payments/purchase/${entrypoint}`, payload, config));
        return handleResponse(err, resp, getPurchaseResponseSchema);
    }
    /**
     * @param bottegaCookieHeader - this arg must be passed in the full header form 'bottega=xxxxxxxxx'
     */
    async getPurchaseToken(bottegaCookieHeader) {
        const [err, resp] = await to(this.httpClient.get(`/payments/purchase_token`, {
            withCredentials: true,
            headers: {
                Cookie: bottegaCookieHeader,
            },
        }));
        return handleResponse(err, resp, getPurchaseTokenResponseSchema);
    }
    /**
     * @param bottegaCookieHeader - this arg must be passed in the full header form 'bottega=xxxxxxxxx'
     */
    async getBillingInfo(userID, bottegaCookieHeader) {
        const [err, resp] = await to(this.httpClient.get(`/users/${userID}/billing_info`, {
            withCredentials: true,
            headers: {
                Cookie: bottegaCookieHeader,
            },
        }));
        return handleResponse(err, resp, getBillingInfoResponseSchema);
    }
    async setBillingInfo(userId, billingInfo) {
        const body = morphism(setBillingInfoRawSchema, billingInfo);
        const config = {
            withCredentials: true,
        };
        const [err, resp] = await to(this.httpClient.put(`/users/${userId}/billing_info`, body, config));
        return handleResponse(err, resp, getBillingInfoResponseSchema);
    }
}

const baseGeoSchema = {
    key: 'id',
    uri: 'uri',
    value: 'value',
    label: 'label',
    level: 'level',
    friendly_name: 'friendlyName',
};
const regionSchemaRest = {
    ...baseGeoSchema,
    neighbors: 'neighbors',
};
const citySchemaRest = {
    ...baseGeoSchema,
    istat: 'istat',
    short_name: 'shortName',
    region_id: 'regionId',
};
const townSchemaRest = {
    ...baseGeoSchema,
    istat: 'istat',
    city_id: 'cityId',
    has_zone: 'hasZone',
    region_id: 'regionId',
};
const itemGeoSchema$1 = createSchema({
    region: {
        path: 'region',
        fn: morphism(regionSchemaRest),
    },
    city: {
        path: 'city',
        fn: morphism(citySchemaRest),
    },
    town: {
        path: 'town',
        fn: morphism(townSchemaRest),
    },
}, { undefinedValues: { strip: true } });
const itemPhoneSchema$1 = {
    show: 'show',
    number: 'number',
    idd_prefix: 'prefix',
};
const reversePhoneVerificationChallengeSchema = {
    phone_number: 'number',
    idd_prefix: 'prefix',
    source: 'source',
};
const reversePhoneVerificationVerifySchema = {
    challenge_id: 'id',
    phone_number: 'number',
    idd_prefix: 'prefix',
    otp: 'otp',
};

const itemGeoSchema = createSchema({
    region: {
        path: 'region',
        fn: morphism(regionSchema),
    },
    city: {
        path: 'city',
        fn: morphism(citySchema),
    },
    town: {
        path: 'town',
        fn: morphism(townSchema),
    },
}, { undefinedValues: { strip: true } });
const itemPhoneSchema = {
    show: 'show',
    number: 'number',
    prefix: 'idd_prefix',
};
const ProfileDataSchema = createSchema({
    username: 'name',
    birthdate: 'borndate',
    sex: 'gender',
    geo: {
        path: 'geo',
        fn: morphism(itemGeoSchema),
    },
    phone: {
        path: 'phone',
        fn: morphism(itemPhoneSchema),
    },
}, { undefinedValues: { strip: true } });
const ProfileQueryDataSchema = {
    username: 'name',
    isDefaultName: 'is_default_name',
    picture: 'picture',
    showPhone: 'show_phone',
};
const getProfileResponseSchema = {
    ...genericResponseSchema,
    userProfile: {
        path: 'data',
        fn: morphism(ProfileDataSchema),
    },
};
const getProfileQueryResponseSchema = {
    ...genericResponseSchema,
    userProfile: {
        path: 'data',
        fn: morphism(ProfileQueryDataSchema),
    },
};
const phoneVerificationStatusSchema = {
    remainingAttempts: 'remaining_attempts',
    isVerified: 'is_verified',
};
const phoneVerificationStatusResponseSchema = {
    ...genericResponseSchema,
    phoneVerificationStatus: {
        path: 'data',
        fn: morphism(phoneVerificationStatusSchema),
    },
};
const phoneVerificationChallengeSchema = {
    remainingAttempts: 'remaining_attempts',
    id: 'challenge_id',
    channel: 'channel',
    otpDuration: 'otp_duration',
};
const phoneVerificationChallengeResponseSchema = {
    ...genericResponseSchema,
    phoneVerificationChallenge: {
        path: 'data',
        fn: morphism(phoneVerificationChallengeSchema),
    },
};
const userActivationStatusSchema = {
    secret: 'secret',
    trackingId: 'tracking_id',
    userId: 'user_id',
};
const userActivationStatusResponseSchema = {
    ...genericResponseSchema,
    activationStatus: {
        path: 'data',
        fn: morphism(userActivationStatusSchema),
    },
};

class PolymatheiaClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeout) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1/users', channel, timeout);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async get(userId) {
        const path = `/${userId}/info/profile`;
        const [err, rawResp] = await to(this.httpClient.get(path));
        return handleResponse(err, rawResp, getProfileResponseSchema);
    }
    async getQuery(userId) {
        const path = `/${userId}/info/profile/query`;
        const [err, rawResp] = await to(this.httpClient.get(path));
        return handleResponse(err, rawResp, getProfileQueryResponseSchema);
    }
    async update(userId, profileData, method = 'put') {
        const path = `/${userId}/info/profile`;
        const body = {
            borndate: profileData.birthdate,
            gender: profileData.sex,
            name: profileData.username,
            geo: morphism(itemGeoSchema$1, profileData.geo),
            phone: morphism(itemPhoneSchema$1, profileData.phone),
        };
        const [err, rawResp] = await to(this.httpClient[method](path, body, {
            withCredentials: true,
        }));
        return handleResponse(err, rawResp, getProfileResponseSchema);
    }
    async phoneVerificationStatus(userID, jwt) {
        const authHeader = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
        const url = `/${userID}/phone-verification/status`;
        const [err, resp] = await to(this.httpClient.get(url, { headers: authHeader }));
        return handleResponse(err, resp, phoneVerificationStatusResponseSchema);
    }
    async phoneVerificationChallenge(userID, phoneVerificationChallengeData, jwt) {
        const authHeader = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
        const body = morphism(reversePhoneVerificationChallengeSchema, phoneVerificationChallengeData);
        const [err, resp] = await to(this.httpClient.post(`/${userID}/phone-verification/challenge`, body, {
            withCredentials: true,
            headers: authHeader,
        }));
        return handleResponse(err, resp, phoneVerificationChallengeResponseSchema);
    }
    async phoneVerificationVerify(userID, phoneVerificationVerifyData, jwt) {
        const authHeader = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
        const body = morphism(reversePhoneVerificationVerifySchema, phoneVerificationVerifyData);
        const [err, resp] = await to(this.httpClient.post(`/${userID}/phone-verification/verify`, body, {
            withCredentials: true,
            headers: authHeader,
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
    async userActivationStatus(userID, jwt) {
        const authHeader = jwt ? { Authorization: `Bearer ${jwt}` } : undefined;
        const [err, resp] = await to(this.httpClient.put(`/${userID}/info/activation-status`, {
            status: 'ACTIVE',
        }, {
            headers: authHeader,
        }));
        return handleResponse(err, resp, userActivationStatusResponseSchema);
    }
}

const RelatedAdSchema = {
    urn: 'urn',
    subject: 'subject',
    body: 'body',
    images: 'images',
    urls: 'urls',
    type: 'type',
    category: {
        path: 'category',
        fn: morphism(itemCategorySchema),
    },
    features: {
        path: 'features',
        fn: (features) => features.reduce((feats, rawFeature) => ({
            ...feats,
            [rawFeature.uri]: rawFeature,
        }), {}),
    },
    geo: {
        path: 'geo',
        fn: morphism(itemGeoSchema$2),
    },
};
const RelatedTrackingInfoSchema = {
    transactionId: 'transaction-id',
    listName: 'list-name',
    itemId: 'item-id',
};
const getFavoritesResponseSchema = {
    ...genericResponseSchema,
    relatedAds: {
        path: 'data.related-items',
        fn: morphism(RelatedAdSchema),
    },
    trackingInfo: {
        path: 'data.tracking-info',
        fn: morphism(RelatedTrackingInfoSchema),
    },
};
const listRelatedIdsSchema = {
    ...genericResponseSchema,
    relatedAdsIds: 'data.related-items',
    trackingInfo: {
        path: 'data.tracking-info',
        fn: morphism(RelatedTrackingInfoSchema),
    },
};

class PorosClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeout) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v1', channel, timeout);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async listCorrelatedIds(listId, userId, source) {
        const params = { tracking_id: userId, source };
        const [err, rawResp] = await to(this.httpClient.get(`/correlated/correlated_items/${listId}/ids`, {
            params,
        }));
        return handleResponse(err, rawResp, listRelatedIdsSchema);
    }
    /**
     * @deprecated use `listCorrelatedIds` and then do a search on anubis instead
     */
    async getCorrelatedListIds(listId, userId, source) {
        const path = `/cors/correlated/correlated_items/${listId}`;
        const params = {};
        params['tracking_id'] = userId;
        params['source'] = source;
        const [err, rawResp] = await to(this.httpClient.get(path, { params }));
        return handleResponse(err, rawResp, getFavoritesResponseSchema);
    }
}

const DEFAULT_URL = '/annunci-italia/vendita/usato/';
/**
 * @deprecated This logic should not be used.
 */
function buildListingURL(params) {
    if (!params) {
        return DEFAULT_URL;
    }
    const type = getDefaultType(params.category);
    const category = getCategoryFriendly(params.category);
    const query = getQuery(params.query);
    if (params?.radiusSearch) {
        return `/annunci-locali/${type}/${category}/${query}`;
    }
    else {
        const region = getRegion(params.geo, params.includeNearbyRegions);
        const city = getCity(params?.geo);
        const town = getTown(params?.geo);
        return `/annunci-${region}/${type}/${category}/${city}${town}${query}`;
    }
}
function getRegion(geo, includeNearbyRegions = false) {
    if (!(geo && geo.region && geo.region.friendlyName)) {
        return 'italia';
    }
    if (includeNearbyRegions && !(geo.city || geo.town)) {
        return `${geo.region.friendlyName}-vicino`;
    }
    return geo.region.friendlyName;
}
function getDefaultType(category) {
    if (!(category && category.id)) {
        return 'vendita';
    }
    switch (category.id) {
        case CategoryId.CamerePostiLetto:
            return 'affitto';
        case CategoryId.CaseVacanza:
            return 'affitto-vacanze';
        default:
            return 'vendita';
    }
}
function getCategoryFriendly(category) {
    if (!(category && category.friendly)) {
        return 'usato';
    }
    return category.friendly;
}
function getCity(geo) {
    if (!(geo && geo.city && geo.city.friendlyName)) {
        return '';
    }
    return `${geo.city.friendlyName}/`;
}
function getTown(geo) {
    if (!(geo && geo.town && geo.town.friendlyName)) {
        return '';
    }
    return `${geo.town.friendlyName}/`;
}
function getQuery(query) {
    if (!query) {
        return '';
    }
    const trimmedQuery = query.trim();
    const encodedQuery = encodeURIComponent(trimmedQuery);
    return `?q=${encodedQuery}`;
}

const thresholdSchema = {
    userId: 'user_id',
    categoryId: 'category_id',
    isOver: 'is_over',
    numAds: 'num_ads',
    threshold: 'threshold',
    typeProvider: 'type_provider',
    referenceId: 'reference_id',
};
const getThresholdSchema = {
    ...genericResponseSchema,
    threshold: {
        path: 'data',
        fn: morphism(thresholdSchema),
    },
};

class TerminusClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeout) {
        const args = [baseUrl, '/v1/threshold', channel, timeout];
        const clientAndConfig = clientFactoryAndConfig(...args);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    async getThreshold(userId, categoryId) {
        const [err, resp] = await to(this.httpClient.get(`/user/${userId}/category/${categoryId}`, {
            withCredentials: true,
        }));
        return handleResponse(err, resp, getThresholdSchema);
    }
}

const AdvResponseSchema = {
    ...genericResponseSchema,
    adv: 'data.adv',
};

class TextLinkClient {
    httpClient;
    constructor(baseUrl, apiPath, channel) {
        this.httpClient = clientFactory(baseUrl, apiPath, channel);
    }
    async searchAdv(name, adv_type, category, region, city) {
        const params = new URLSearchParams({ adv_type, category, region, city });
        const resp = await this.httpClient.get(`${name}?${params.toString()}`);
        return morphism(AdvResponseSchema, resp);
    }
}

const autocompletePredictionSchema = {
    ...genericResponseSchema,
    predictions: 'data',
};

class TiresiasClient {
    httpClient;
    axiosConfig;
    constructor(baseUrl, channel, timeoutMs = 500) {
        const clientAndConfig = clientFactoryAndConfig(baseUrl, '/v2/autocomplete', channel, timeoutMs);
        this.axiosConfig = clientAndConfig.config;
        this.httpClient = clientAndConfig.instance;
    }
    /**
     * @param keyword - Query string to ask Tiresias the prediction for. Min length: 2
     * @param threshold - Results below this probability will be filtered out. Float. Min: 0, Max: 1
     */
    async predict(keyword, threshold = 0, useNextVersion, headers) {
        const url = useNextVersion ? '/predict/next' : '/predict';
        const [err, resp] = await to(this.httpClient.get(url, {
            params: {
                s: keyword,
                min: threshold,
            },
            headers,
        }));
        return handleResponse(err, resp, autocompletePredictionSchema);
    }
}

const userInfoSchema = {
    reputation: 'reputation',
    identity: 'identity',
    communication: 'communication',
    presence: 'presence',
    badges: 'badges',
    links: '_links',
    replyTimeText: 'replyTimeText',
};
const userSettingsSchema = {
    externalUserId: 'externalUserId',
    enabled: 'allowPresenceDisplay',
};
const getProfileTrustResponseSchema = {
    ...genericResponseSchema,
    trustProfileInfo: {
        path: 'data',
        fn: morphism(userInfoSchema),
    },
};
const getProfileTrustSettingsResponseSchema = {
    ...genericResponseSchema,
    trustProfileSettings: {
        path: 'data',
        fn: morphism(userSettingsSchema),
    },
};

class TrustClient {
    httpClient;
    constructor(baseUrl, timeout) {
        this.httpClient = clientFactory(baseUrl, '', undefined, timeout);
    }
    async getProfileTrustInfo(userID) {
        const [err, resp] = await to(this.httpClient.get(`/profile/sdrn:subito:user:${userID}`));
        return handleResponse(err, resp, getProfileTrustResponseSchema);
    }
    async getProfileTrustSettings(userID, jwt) {
        const [err, resp] = await to(this.httpClient.get(`/profile/sdrn:subito:user:${userID}/settings`, {
            headers: { Authorization: `Bearer ${jwt}` },
        }));
        return handleResponse(err, resp, getProfileTrustSettingsResponseSchema);
    }
    async updateProfileTrustSettings(userID, jwt, enabled) {
        const [err, resp] = await to(this.httpClient.patch(`/profile/sdrn:subito:user:${userID}/settings`, {
            allowPresenceDisplay: enabled,
        }, {
            headers: { Authorization: `Bearer ${jwt}` },
        }));
        return handleResponse(err, resp, genericResponseSchema);
    }
}

class TycheClient {
    httpClient;
    constructor(baseUrl, channel) {
        this.httpClient = clientFactory(baseUrl, '/', channel);
    }
    async postPromoteAd(ad_id, admin_id, paid_option, selling_point) {
        const [err, resp] = await to(this.httpClient.post(`/v1/paidoption/item/${ad_id}/option/${paid_option}/admin/${admin_id}?selling_point=${selling_point}`));
        return handleGenericResponse(err, resp);
    }
}

const UsersInViolationSchema = {
    UserId: 'user_id',
    CategoryId: 'category_id',
    CategoryName: 'category_name',
    LimitAdsCount: 'limit_ads_count',
    TotalAdsCount: 'total_ads_count',
    ArchivedDate: 'archived_date',
};
const AdsByUserAndCategorySchema = {
    AdId: 'ad_id',
    ListId: 'list_id',
    UserId: 'user_id',
    CategoryId: 'category_id',
    ListTime: 'list_time',
    ApprovedTime: 'approved_time',
};
const getUsersInViolationSchema = {
    ...genericResponseSchema,
    users: {
        path: 'data',
        fn: morphism(UsersInViolationSchema),
    },
};
const getAdsByUserAndCategorySchema = {
    ...genericResponseSchema,
    ads: {
        path: 'data',
        fn: morphism(AdsByUserAndCategorySchema),
    },
};

const commonPath = '/v1';
class YamaClient {
    httpClient;
    constructor(baseUrl, channel, timeout) {
        this.httpClient = clientFactory(baseUrl, '/', channel, timeout);
    }
    async getUsersInViolation(params) {
        const [err, rawResp] = await to(this.httpClient.get(`${commonPath}/users-in-violation`, { params }));
        return handleResponse(err, rawResp, getUsersInViolationSchema);
    }
    async getAdsByUserAndCategory(params) {
        const [err, rawResp] = await to(this.httpClient.get(`${commonPath}/ads`, { params }));
        return handleResponse(err, rawResp, getAdsByUserAndCategorySchema);
    }
}

export { AdTypes, AdonisClient, AdvertiserType, AeolusClient, AgonClient, AidosClient, AngeliaClient, AnubisClient, ApolloClient, AresClient, AtroposClient, AvailableFee, AvailablePublicationFee, CacusClient, CaerusClient, CategoryId, CategoryStore, ChrysusClient, CloacinaClient, ClothoBackOfficeClient, ClothoClient, DikeClient, DioneClient, DysnomiaClient, EchoClient, Entrypoint, FeatureURI, GDPRRequestStatus, GDPRRequestType, GaneshaClient, GenericResponse, GeoLevel, GeogosClient, GeogosToAutocomplete, HTTPStatusCode, HebeCampaignPage, HebeCampaignPosition, HebeCampaignProperty, HebeCampaignType, HebeClient, HebeClientV2, HephaestusClient, HeraclesClient, HereMapsClient, HermesClient, HerseClient, HestiaClient, HyperwalletClient, ImageSize, IrisClient, LavernaClient, LetheClient, MacroCategoryStore, MetiClient, MnemeClient, MnemosyneClient, NarcissusClient, OboloClient, OrcusClient, OrderByDirection, Orders, PaidOptionEntryPoint, PersephoneClient, PhaethonClient, PitoClient, PlutusClient, PolymatheiaClient, PorosClient, PostField, PresenceStatus, PrivateBannerEntryPoints, PrivateBannerPages, State, TerminusClient, TextLinkClient, TiresiasClient, TrustClient, TuttaItalia, TycheClient, TypeProvider, UserState, YamaClient, applyDiscount, buildListingURL, clientFactory, clientFactoryAndConfig, getGeoInclusionLabel, getGeoLevel, getLabelFromGeo, handleGenericResponse, handleResponse, invalidRequestError, invalidResponseError, isCategory, isMacroCategory, notFoundResponseError, requestTimeoutResponseError, to, unknownError };
//# sourceMappingURL=index.js.map
